import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react'
import { useAuth } from '../../contexts/AuthContext'
import { useGame } from '../../contexts/GameContext'
import { useWebSocket } from '../../contexts/WebSocketContext'
import ChessBoard from '../Chess/ChessBoard'
import UserDisplay from '../UserDisplay/UserDisplay'
import soundManager from '../../utils/soundUtils'
import './Game.css'

const PROMOTION_LABELS = {
  Q: 'Queen',
  R: 'Rook',
  B: 'Bishop',
  N: 'Knight'
}

const PIECE_SYMBOLS = {
  'K': '♔', 'Q': '♕', 'R': '♖', 'B': '♗', 'N': '♘', 'P': '♙',
  'k': '♚', 'q': '♛', 'r': '♜', 'b': '♝', 'n': '♞', 'p': '♟'
}

// Load piece images for captured pieces display
const loadPieceImages = (theme) => {
  try {
    if (theme === 'cyberpunk') {
      return {
        'K': require('../../assets/images/pieces/cyberpunk/kingwhite.png'),
        'Q': require('../../assets/images/pieces/cyberpunk/queenwhite.png'),
        'R': require('../../assets/images/pieces/cyberpunk/rookwhite.png'),
        'B': require('../../assets/images/pieces/cyberpunk/bishopwhite.png'),
        'N': require('../../assets/images/pieces/cyberpunk/knightwhite.png'),
        'P': require('../../assets/images/pieces/cyberpunk/pawnwhite.png'),
        'k': require('../../assets/images/pieces/cyberpunk/kingblack.png'),
        'q': require('../../assets/images/pieces/cyberpunk/queenblack.png'),
        'r': require('../../assets/images/pieces/cyberpunk/rookblack.png'),
        'b': require('../../assets/images/pieces/cyberpunk/bishopblack.png'),
        'n': require('../../assets/images/pieces/cyberpunk/knightblack.png'),
        'p': require('../../assets/images/pieces/cyberpunk/pawnblack.png')
      }
    }
    if (theme === '2') {
      return {
        'K': require('../../assets/images/pieces/2/whiteking.png'),
        'Q': require('../../assets/images/pieces/2/whitequeen.png'),
        'R': require('../../assets/images/pieces/2/whiterook.png'),
        'B': require('../../assets/images/pieces/2/whitebishop.png'),
        'N': require('../../assets/images/pieces/2/whiteknight.png'),
        'P': require('../../assets/images/pieces/2/whitepawn.png'),
        'k': require('../../assets/images/pieces/2/blackking.png'),
        'q': require('../../assets/images/pieces/2/blackqueen.png'),
        'r': require('../../assets/images/pieces/2/blackrook.png'),
        'b': require('../../assets/images/pieces/2/blackbishop.png'),
        'n': require('../../assets/images/pieces/2/blackknight.png'),
        'p': require('../../assets/images/pieces/2/blackpawn.png')
      }
    }
    // Default to classic theme
    return {
      'K': require('../../assets/images/pieces/classic/white-king.png'),
      'Q': require('../../assets/images/pieces/classic/white-queen.png'),
      'R': require('../../assets/images/pieces/classic/white-rook.png'),
      'B': require('../../assets/images/pieces/classic/white-bishop.png'),
      'N': require('../../assets/images/pieces/classic/white-knight.png'),
      'P': require('../../assets/images/pieces/classic/white-pawn.png'),
      'k': require('../../assets/images/pieces/classic/black-king.png'),
      'q': require('../../assets/images/pieces/classic/black-queen.png'),
      'r': require('../../assets/images/pieces/classic/black-rook.png'),
      'b': require('../../assets/images/pieces/classic/black-bishop.png'),
      'n': require('../../assets/images/pieces/classic/black-knight.png'),
      'p': require('../../assets/images/pieces/classic/black-pawn.png')
    }
  } catch (error) {
    console.error('Error loading piece images:', error)
    return {}
  }
}

// Component to display a piece image
const PieceImage = ({ piece, theme, className }) => {
  const pieceImages = useMemo(() => loadPieceImages(theme), [theme])
  const imageSrc = pieceImages[piece]

  if (!imageSrc) {
    // Fallback to symbol if image not found
    return <span className={className}>{PIECE_SYMBOLS[piece] || '?'}</span>
  }

  return (
    <img
      src={imageSrc}
      alt={piece}
      className={className}
      draggable={false}
    />
  )
}

const MOBILE_BREAKPOINT = 1000

const Game = ({ gameId, onBack, onViewReplay, onNavigateToGame, sessionWins = {}, onUpdateSessionWins }) => {
  const { user, session } = useAuth()
  const {
    currentGame,
    getGame,
    resignGame,
    error,
    setCurrentGame,
    listGames,
    choosePromotion,
    evaluateGame,
    offerDraw,
    cancelDraw,
    respondDraw,
    getProfile,
    deleteGame,
    markReady
  } = useGame()
  const { joinGame, sendMove, sendMessage, addMessageHandler, removeMessageHandler, isConnected, connectionStatus, connect, subscribe } = useWebSocket()
  const [selectedSquare, setSelectedSquare] = useState(null)
  const [legalMoves, setLegalMoves] = useState([])

  // Temporary history navigation stubs (to be removed)
  const [isViewingHistory] = useState(false)
  const [historyIndex] = useState(null)
  const [historicalBoard] = useState(null)
  const [historicalSpecialSquares] = useState(null)
  const setIsViewingHistory = () => {}
  const setHistoryIndex = () => {}
  const setHistoricalBoard = () => {}
  const setHistoricalSpecialSquares = () => {}
  const navigateToMove = () => {}
  const navigateHistory = () => {}
  const returnToLive = () => {}
  const reconstructBoardAtMove = () => null
  const getSpecialSquaresForMove = () => ({ red: [], blue: [], green: [] })



  // State for game cancellation notification
  const [showCancellationPopup, setShowCancellationPopup] = useState(false)
  const [cancellationMessage, setCancellationMessage] = useState('')


  const [moveError, setMoveError] = useState(null)
  const [optimisticGame, setOptimisticGame] = useState(null)
  const [pendingMove, setPendingMove] = useState(null)
  const [promotionPrompt, setPromotionPrompt] = useState(null)
  const [lastMove, setLastMove] = useState(null)
  const [teleportPrompt, setTeleportPrompt] = useState(null)
  const [boardOrientation, setBoardOrientation] = useState('white')
  const [pieceTheme, setPieceTheme] = useState('classic')
  const [boardTheme, setBoardTheme] = useState('riftchess')
  const [viewportWidth, setViewportWidth] = useState(() => (typeof window === 'undefined' ? 1024 : window.innerWidth))
  const [isMobileView, setIsMobileView] = useState(() => viewportWidth <= MOBILE_BREAKPOINT)
  const [isCancellingGame, setIsCancellingGame] = useState(false)
  const pieceImages = useMemo(() => loadPieceImages(pieceTheme), [pieceTheme])
  const pendingMoveRef = useRef(null)
  const pendingMoveRecoveryRef = useRef({ lastTriggeredAt: 0, attempts: 0 })
  const [lastResultGameId, setLastResultGameId] = useState(null)
  const waitingForOpponent = Boolean(
    currentGame &&
    (!currentGame.white_player || !currentGame.black_player) &&
    !['checkmate', 'stalemate', 'draw', 'resignation', 'timeout', 'abandoned'].includes(currentGame.status)
  )
  const isCurrentUserInGame = Boolean(
    currentGame &&
    (currentGame.white_player_id === user?.id || currentGame.black_player_id === user?.id)
  )
  const isSocketConnected = connectionStatus === 'connected'
  const connectionStatusClass = isSocketConnected ? 'online' : 'offline'
  const connectionStatusLabel = isSocketConnected ? 'Connected' : 'Disconnected'
  const isReconnecting = connectionStatus === 'connecting' || connectionStatus === 'error' || connectionStatus === 'disconnected'

  // Piece animation and red square timing state
  const [animatingMove, setAnimatingMove] = useState(null) // { from, to, piece, startTime }
  const [waitingForRedSquares, setWaitingForRedSquares] = useState(false)
  const animationTimeoutRef = useRef(null)

  // Rematch state
  const [rematchStatus, setRematchStatus] = useState(null) // null, 'requested', 'received', 'accepted', 'declined'
  const [rematchRequestedBy, setRematchRequestedBy] = useState(null)
  const [nowTick, setNowTick] = useState(Date.now())
  const [showResignModal, setShowResignModal] = useState(false)
  const [showDrawModal, setShowDrawModal] = useState(false)
  const [showRulesModal, setShowRulesModal] = useState(false)
  const [selectedRule, setSelectedRule] = useState(null)
  const [showRematchModal, setShowRematchModal] = useState(false)
  const evaluationPendingRef = useRef(false)
  const moveTimeoutRef = useRef(null)
  const cancellationShownRef = useRef(false)

  const pregameTimeoutTriggeredRef = useRef(false)
  const [showIncomingDrawModal, setShowIncomingDrawModal] = useState(false)

  useEffect(() => {
    pendingMoveRef.current = pendingMove
  }, [pendingMove])

  useEffect(() => {
    if (typeof window === 'undefined') return undefined

    const handleResize = () => {
      const width = window.innerWidth
      const shouldUseMobile = width <= MOBILE_BREAKPOINT
      setViewportWidth(prevWidth => (prevWidth === width ? prevWidth : width))
      setIsMobileView(prev => (prev === shouldUseMobile ? prev : shouldUseMobile))
    }

    handleResize()
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  useEffect(() => {
    return () => {
      if (moveTimeoutRef.current) {
        clearTimeout(moveTimeoutRef.current)
        moveTimeoutRef.current = null
      }
      if (animationTimeoutRef.current) {
        clearTimeout(animationTimeoutRef.current)
        animationTimeoutRef.current = null
      }
    }
  }, [])

  // Load themes from profile API on mount and listen for changes
  useEffect(() => {
    const loadInitialThemes = async () => {
      if (session?.access_token) {
        try {
          const profile = await getProfile()
          if (profile.piece_theme) {
            setPieceTheme(profile.piece_theme)
          }
          if (profile.board_theme) {
            // Handle migration from 'purple' and 'darkgrey' to 'riftchess'
            const boardTheme = (profile.board_theme === 'purple' || profile.board_theme === 'darkgrey') ? 'riftchess' : profile.board_theme
            setBoardTheme(boardTheme)
          }
        } catch (err) {
          console.error('Failed to load theme preferences:', err)
          // Fallback to localStorage
          const savedPieceTheme = localStorage.getItem('riftchess-piece-theme')
          const savedBoardTheme = localStorage.getItem('riftchess-board-theme')
          if (savedPieceTheme) setPieceTheme(savedPieceTheme)
          if (savedBoardTheme) {
            // Handle migration from 'purple' and 'darkgrey' to 'riftchess'
            const boardTheme = (savedBoardTheme === 'purple' || savedBoardTheme === 'darkgrey' || savedBoardTheme === 'classic') ? 'riftchess' : savedBoardTheme
            setBoardTheme(boardTheme)
          }
        }
      } else {
        // Fallback to localStorage for non-authenticated users
        const savedPieceTheme = localStorage.getItem('riftchess-piece-theme')
        const savedBoardTheme = localStorage.getItem('riftchess-board-theme')
        if (savedPieceTheme) setPieceTheme(savedPieceTheme)
        if (savedBoardTheme) {
          // Handle migration from 'purple' and 'darkgrey' to 'riftchess'
          const boardTheme = (savedBoardTheme === 'purple' || savedBoardTheme === 'darkgrey') ? 'riftchess' : savedBoardTheme
          setBoardTheme(boardTheme)
        }
      }
    }

    loadInitialThemes()

    // Listen for theme changes from GamePreferences
    const handlePieceThemeChange = (event) => {
      setPieceTheme(event.detail)
    }

    const handleBoardThemeChange = (event) => {
      setBoardTheme(event.detail)
    }

    window.addEventListener('pieceThemeChanged', handlePieceThemeChange)
    window.addEventListener('boardThemeChanged', handleBoardThemeChange)

    return () => {
      window.removeEventListener('pieceThemeChanged', handlePieceThemeChange)
      window.removeEventListener('boardThemeChanged', handleBoardThemeChange)
    }
  }, [session, getProfile])

  useEffect(() => {
    cancellationShownRef.current = showCancellationPopup
  }, [showCancellationPopup])

  const clearOptimisticState = useCallback(() => {
    pendingMoveRef.current = null
    pendingMoveRecoveryRef.current = { lastTriggeredAt: 0, attempts: 0 }
    setOptimisticGame(null)
    setPendingMove(null)
    if (moveTimeoutRef.current) {
      clearTimeout(moveTimeoutRef.current)
      moveTimeoutRef.current = null
    }
  }, [setOptimisticGame, setPendingMove])

  const triggerPendingMoveRecovery = useCallback((reason) => {
    const pendingMove = pendingMoveRef.current
    if (!pendingMove || !currentGame?.id) {
      return
    }

    const now = Date.now()
    const { lastTriggeredAt, attempts } = pendingMoveRecoveryRef.current
    if (now - lastTriggeredAt < 1200) {
      console.log('[GAME] Skipping rapid pending-move recovery trigger', reason)
      return
    }

    pendingMoveRecoveryRef.current = {
      lastTriggeredAt: now,
      attempts: attempts + 1
    }

    console.warn('[GAME] Pending move recovery triggered', { reason, attempts: attempts + 1, move: pendingMove })
    if (connect) connect()
    if (joinGame) joinGame(currentGame.id)
    const success = sendMove(currentGame.id, pendingMove)
    if (!success) {
      setMoveError('Connection issue: retrying your last move…')
    } else {
      setMoveError('Resent your move. Waiting for confirmation…')
    }
  }, [connect, currentGame?.id, joinGame, sendMove, setMoveError])

  // Check if it's current player's turn
  const isPlayerTurn = useMemo(() => {
    if (!currentGame || !user) {
      return false
    }

    const isPlayerWhite = currentGame.white_player_id === user.id
    const isPlayerBlack = currentGame.black_player_id === user.id

    if (!isPlayerWhite && !isPlayerBlack) {
      return false
    }

    // During pregame, only white player can move to start the game
    if (currentGame.status === 'pregame') {
      return isPlayerWhite
    }

    const result = (currentGame.current_turn === 'white' && isPlayerWhite) ||
           (currentGame.current_turn === 'black' && isPlayerBlack)

    return result
  }, [currentGame, user])

  // Load game on mount and join WebSocket room
  useEffect(() => {
    if (gameId && (!currentGame || currentGame.id !== gameId)) {
      getGame(gameId)
    }
  }, [gameId, currentGame, getGame])

  // Clear board state when navigating to a new game
  useEffect(() => {
    // Reset board-related state when game ID changes
    setSelectedSquare(null)
    setLegalMoves([])
    setLastMove({ from: null, to: null })
    setMoveError(null)
    setRematchStatus(null)
    setRematchRequestedBy(null)
  }, [gameId])

  // Join WebSocket game room immediately when gameId and connection are available
  // Join WebSocket game room immediately when gameId and connection are available
  useEffect(() => {
    if (gameId && isConnected && joinGame) {
      console.log('Connectivity restored: Re-joining room and syncing state')
      joinGame(gameId)
      // CRITICAL FIX: Always fetch the latest authoritative state when (re)connecting.
      // This ensures we catch up on any moves that happened while we were disconnected.
      getGame(gameId)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameId, isConnected, joinGame])

  // Handle WebSocket reconnection for Android devices
  useEffect(() => {
    const handleWebSocketReconnected = () => {
      const isAndroid = /Android/i.test(navigator.userAgent)

      if (isAndroid && gameId) {

        // Refresh the game state
        getGame(gameId)

        // Rejoin the game room
        if (joinGame) {
          setTimeout(() => {
            joinGame(gameId)
          }, 1000)
        }
      }
    }

    window.addEventListener('websocket-reconnected', handleWebSocketReconnected)
    return () => window.removeEventListener('websocket-reconnected', handleWebSocketReconnected)
  }, [gameId, getGame, joinGame])

  useEffect(() => {
    evaluationPendingRef.current = false
  }, [currentGame?.id])

  useEffect(() => {
    if (!currentGame || !user) {
      setShowIncomingDrawModal(false)
      return
    }

    const pendingOfferBy = currentGame.pending_draw_offer_by
    if (
      pendingOfferBy &&
      pendingOfferBy !== user.id &&
      currentGame.status === 'active'
    ) {
      setShowIncomingDrawModal(true)
    } else if (!pendingOfferBy || currentGame.status !== 'active') {
      setShowIncomingDrawModal(false)
    }
  }, [currentGame?.pending_draw_offer_by, currentGame?.status, user?.id])

  useEffect(() => {
    if (currentGame?.status !== 'active') {
      setShowDrawModal(false)
      setShowIncomingDrawModal(false)
    }
  }, [currentGame?.status])

  // Join WebSocket game room when game is loaded
  useEffect(() => {
    if (currentGame && isConnected) {
      joinGame(currentGame.id)
    }
  }, [currentGame?.id, isConnected, joinGame])

  // Fallback: refresh game state when status changes from waiting (to catch bot joins if WebSocket fails)
  useEffect(() => {
    if (currentGame && currentGame.status === 'pregame' && getGame) {
      // Only refresh once when game transitions to pregame (likely from a bot join)
      const timeoutId = setTimeout(() => {
        getGame(currentGame.id)
      }, 1000) // Wait 1 second to allow WebSocket message to arrive first

      return () => clearTimeout(timeoutId)
    }
  }, [currentGame?.status, currentGame?.id, getGame])

  // Handler for real-time move updates (for efficiency)
  const handleMoveUpdate = useCallback((message) => {
    if (message.type === 'move_update' && message.game_id === currentGame?.id) {
      console.log('[GAME] move_update received', {
        gameId: message.game_id,
        move: message.move,
        moveCount: message.move_count,
        pendingMove: pendingMoveRef.current
      })

      // Update game state with move data efficiently
      setCurrentGame(prevGame => ({
        ...prevGame,
        board: message.current_board,
        current_turn: message.current_turn,
        move_count: message.move_count,
        white_time: message.white_time_remaining,
        black_time: message.black_time_remaining,
        last_move_time: message.last_move_time,
        status: message.status,
        red_squares: message.red_squares,
        blue_squares: message.blue_squares,
        green_squares: message.green_squares,
        visual_effects: message.visual_effects,
        result: message.result,  // Sync result to avoid race condition with game completion
        end_reason: message.end_reason, // Sync end reason for detailed display
        white_rating_before: message.white_rating_before,
        white_rating_after: message.white_rating_after,
        black_rating_before: message.black_rating_before,
        black_rating_after: message.black_rating_after
      }))

      // Update last move for board highlighting
      if (message.move) {
        setLastMove(message.move)
      }

      // Play sound for opponent moves (not our own)
      const isOwnMove = pendingMoveRef.current &&
                       pendingMoveRef.current.from === message.move?.from &&
                       pendingMoveRef.current.to === message.move?.to

      if (message.move && !isOwnMove) {
        const isCapture = message.move.captured
        soundManager.playMoveOrCapture(isCapture)
      }

      // Clear optimistic state if this matches our pending move
      if (pendingMoveRef.current && message.move) {
        const pendingMove = pendingMoveRef.current
        if (pendingMove.from === message.move.from &&
            pendingMove.to === message.move.to &&
            (!pendingMove.promotion || pendingMove.promotion === message.move.promotion)) {
          clearOptimisticState()
        } else {
          console.warn('[GAME] move_update mismatch with pending move', {
            pendingMove,
            serverMove: message.move
          })
          triggerPendingMoveRecovery('move_update_mismatch')
        }
      }

    }
  }, [currentGame?.id, clearOptimisticState, triggerPendingMoveRecovery, user?.id])

  // Set up WebSocket message handlers
  useEffect(() => {
    // Handler for game state updates
    const handleGameState = (message) => {
      if (message.data) {
        try {
          // Check if data is already parsed or needs parsing
          const gameData = typeof message.data === 'string' ? JSON.parse(message.data) : message.data
          console.log('[GAME] game_state message', {
            gameId: message.game_id || gameData?.id || currentGame?.id,
            moveCount: gameData.move_count,
            currentTurn: gameData.current_turn,
            pendingMove: pendingMoveRef.current,
            localMoveCount: currentGame?.move_count
          })

          // Get the last move for later use
          let currentLastMove = null
          if (gameData.moves && gameData.moves.length > 0) {
            currentLastMove = gameData.moves[gameData.moves.length - 1]
          }

          // Update lastMove state for ChessBoard
          setLastMove(currentLastMove)

          // Play sound if this is a new move (move count increased)
          if (currentGame && gameData.move_count > currentGame.move_count) {
            // Try to determine if the last move was a capture
            let isCapture = false
            if (currentLastMove) {
              // Check if the last move had a captured piece
              isCapture = currentLastMove.captured && currentLastMove.captured !== ''
            }


            // Only play sound if this is not our own pending move confirmation
            // (to avoid duplicate sounds for the player who made the move)
            const isOwnMove = pendingMoveRef.current &&
                             lastMove &&
                             pendingMoveRef.current.from === lastMove.from &&
                             pendingMoveRef.current.to === lastMove.to


            if (!isOwnMove) {
              soundManager.playMoveOrCapture(isCapture)
            } else {
            }
          } else {
          }

          // Check if game was cancelled
          if (gameData.status === 'cancelled' && !cancellationShownRef.current) {
            setCancellationMessage('Game was cancelled because white did not make the first move in time.')
            setShowCancellationPopup(true)
          }



          // Animate opponent moves when board updates
          if (currentGame && gameData.last_move && currentLastMove &&
              currentGame.board !== gameData.board) {

            // Check if this is NOT our pending move (i.e., it's opponent's move)
            const isOurPendingMove = pendingMoveRef.current &&
              pendingMoveRef.current.from === currentLastMove.from &&
              pendingMoveRef.current.to === currentLastMove.to

            console.log('🎯 Move detected:', {
              lastMove: currentLastMove,
              pendingMove: pendingMoveRef.current,
              isOurMove: isOurPendingMove
            })

          }

          setCurrentGame(gameData)

          // Clear optimistic state when server confirms our move
          if (pendingMoveRef.current) {
            const pendingMove = pendingMoveRef.current
            let shouldClear = false

            // Check if the last move matches our pending move
            if (currentLastMove && pendingMove.from === currentLastMove.from &&
                pendingMove.to === currentLastMove.to &&
                (!pendingMove.promotion || pendingMove.promotion === currentLastMove.promotion)) {
              shouldClear = true
            }

            // Also check if the move count increased (indicating our move was processed)
            if (currentGame && gameData.move_count > currentGame.move_count) {
              shouldClear = true
            }

            if (shouldClear) {
              clearOptimisticState()
              // Clear red square waiting states when server confirms our move
              setWaitingForRedSquares(false)
            } else if (currentGame && gameData.move_count === currentGame.move_count) {
              triggerPendingMoveRecovery('game_state_mismatch')
            }
          }
          setMoveError(null)
        } catch (err) {
          console.error('Failed to parse game state:', err)
        }
      }
    }

    // Handler for game completion
    const handleGameCompletion = (message) => {
      const { data } = message

      if (data && data.game_id === currentGame?.id) {

        // Show completion notification
        if (data.result) {
          const isPlayerWhite = currentGame.white_player_id === user?.id
          const isPlayerBlack = currentGame.black_player_id === user?.id

          let resultMessage = ''
          let ratingChange = 0

          if (isPlayerWhite) {
            ratingChange = (data.white_rating_after || 0) - (data.white_rating_before || 0)
            if (data.result === 'white_wins') {
              resultMessage = 'You won!'
            } else if (data.result === 'black_wins') {
              resultMessage = 'You lost'
            } else {
              resultMessage = 'Draw'
            }
          } else if (isPlayerBlack) {
            ratingChange = (data.black_rating_after || 0) - (data.black_rating_before || 0)
            if (data.result === 'black_wins') {
              resultMessage = 'You won!'
            } else if (data.result === 'white_wins') {
              resultMessage = 'You lost'
            } else {
              resultMessage = 'Draw'
            }
          }

          setLastResultGameId(data.game_id)
        }

        // Update current game state with completion data to avoid race conditions
        if (currentGame) {
          setCurrentGame(prev => ({
            ...prev,
            status: 'completed',
            result: data.result,
            rated: prev.rated, // Preserve the rated field to ensure rating changes display correctly
            white_rating_before: data.white_rating_before,
            white_rating_after: data.white_rating_after,
            black_rating_before: data.black_rating_before,
            black_rating_after: data.black_rating_after,
            ended_at: data.ended_at
          }))
        }
      } else {
      }
    }

    // Handler for rematch requests
    const handleRematchRequest = (message) => {
      console.log('Received rematch request message:', message)
      const { data } = message
      if (data && data.game_id === currentGame?.id) {
        console.log('Setting rematch status to received from user:', data.requested_by)
        setRematchStatus('received')
        setRematchRequestedBy(data.requested_by)
        setShowRematchModal(true)
      }
    }

    // Handler for rematch responses
    const handleRematchResponse = (message) => {
      const { data } = message
      if (data && data.game_id === currentGame?.id) {
        if (data.declined_by) {
          setRematchStatus('declined')
          setRematchRequestedBy(null)
        }
      }
    }

    // Handler for rematch start
    const handleRematchStart = (message) => {
      const { data } = message
      if (data && data.old_game_id === currentGame?.id) {
        console.log('New rematch game created:', data.new_game_id)

        // Update session wins before navigating
        if (currentGame.status === 'completed' && currentGame.result && onUpdateSessionWins) {
          const newWins = { ...sessionWins }

          // Track wins by player ID, not piece color
          if (currentGame.result === 'white_win' && currentGame.white_player_id) {
            const playerId = currentGame.white_player_id
            newWins[playerId] = (newWins[playerId] || 0) + 1
          } else if (currentGame.result === 'black_win' && currentGame.black_player_id) {
            const playerId = currentGame.black_player_id
            newWins[playerId] = (newWins[playerId] || 0) + 1
          }
          // Note: draws don't increment either counter
          onUpdateSessionWins(newWins)
        }

        // Navigate to the new game
        if (onNavigateToGame) {
          onNavigateToGame(data.new_game_id)
        }

        // Reset rematch status
        setRematchStatus(null)
        setRematchRequestedBy(null)
      }
    }

    // Handler for errors
    const handleError = (message) => {

      // Always rollback optimistic state on any error
      clearOptimisticState()

      if (message.data) {
        try {
          // Check if data is already parsed as object or needs parsing as string
          const errorData = typeof message.data === 'string' ? JSON.parse(message.data) : message.data
          setMoveError(errorData.message)
        } catch (err) {
          console.error('Failed to parse error message:', err)
          // Fallback to display the raw data if parsing fails
          setMoveError('An error occurred')
        }
      } else {
        setMoveError('Move failed')
      }
    }


    // Register subscriptions
    if (subscribe) {
      console.log('Subscribing to WebSocket events for game:', currentGame?.id)
      
      const unsubs = [
        subscribe('game_state', handleGameState),
        subscribe('move_update', handleMoveUpdate),
        subscribe('game_completed', handleGameCompletion),
        subscribe('rematch_request', handleRematchRequest),
        subscribe('rematch_response', handleRematchResponse),
        subscribe('rematch_start', handleRematchStart),
        subscribe('error', handleError)
      ]

      // Cleanup
      return () => {
        unsubs.forEach(unsub => unsub())
      }
    }
  }, [subscribe, setCurrentGame, clearOptimisticState, currentGame?.id, triggerPendingMoveRecovery, user?.id])




  // Handle WebSocket parsing errors by refreshing game state
  useEffect(() => {
    const handleWebSocketError = (event) => {
      const { type } = event.detail
      if (type === 'game_state' && currentGame?.id) {
        getGame(currentGame.id)
      }
    }

    window.addEventListener('websocket-parse-error', handleWebSocketError)
    return () => {
      window.removeEventListener('websocket-parse-error', handleWebSocketError)
    }
  }, [currentGame?.id, getGame])

  // Calculate legal moves for a piece at a given square
  const calculateLegalMoves = (board, square) => {
    const [rank, file] = squareToCoords(square)
    const piece = board[rank][file]

    if (!piece || !isPieceOwnedByPlayer(piece)) return []

    const legalMoves = []
    const isWhite = piece === piece.toUpperCase()
    const pieceType = piece.toLowerCase()

    // Check each square on the board for regular moves
    for (let toRank = 0; toRank < 8; toRank++) {
      for (let toFile = 0; toFile < 8; toFile++) {
        const toSquare = coordsToSquare(toRank, toFile)
        if (isValidMove(board, square, toSquare, currentGame.current_turn)) {
          legalMoves.push(toSquare)
        }
      }
    }

    // If the piece is ALREADY on a blue (teleport) square, also consider teleport destinations
    const isOnBlueSquare = blueSquares?.some(sq => sq.position === square)
    if (isOnBlueSquare) {
      // Check teleportation to any empty square that would resolve check
      for (let toRank = 0; toRank < 8; toRank++) {
        for (let toFile = 0; toFile < 8; toFile++) {
          const toSquare = coordsToSquare(toRank, toFile)

          // Can only teleport to empty squares not on red or green special squares
          if (board[toRank][toFile] !== '') continue
          const onRed = redSquares?.some(sq => sq.position === toSquare)
          const onGreen = greenSquares?.some(sq => sq.position === toSquare)
          // Allow teleporting to blue squares (including staying on the current blue square)
          // but not to red or green squares
          if (onRed || onGreen) continue

          // Check if teleporting to this square would resolve check
          if (!wouldTeleportLeaveKingInCheck(board, rank, file, toRank, toFile)) {
            // Don't add duplicates
            if (!legalMoves.includes(toSquare)) {
              legalMoves.push(toSquare)
            }
          }
        }
      }
    }

    return legalMoves
  }

  // Convert coordinates to square notation
  const coordsToSquare = useCallback((rank, file) => {
    return String.fromCharCode('a'.charCodeAt(0) + file) + (8 - rank)
  }, [])

  const squareToCoords = useCallback((square) => {
    const file = square.charCodeAt(0) - 'a'.charCodeAt(0)
    const rank = 8 - parseInt(square[1])
    return [rank, file]
  }, [])

  const boardToFEN = (board, game) => {
    let fen = ''
    for (let rank of board) {
      let emptyCount = 0
      for (let piece of rank) {
        if (piece === '') {
          emptyCount++
        } else {
          if (emptyCount > 0) {
            fen += emptyCount
            emptyCount = 0
          }
          fen += piece
        }
      }
      if (emptyCount > 0) {
        fen += emptyCount
      }
      fen += '/'
    }
    fen = fen.slice(0, -1)

    const turn = game.current_turn === 'white' ? 'b' : 'w'
    fen += ` ${turn} KQkq - 0 ${Math.floor(game.move_count / 2) + 1}`

    return fen
  }

  const parseFEN = useCallback((fen) => {
    if (!fen) return Array(8).fill().map(() => Array(8).fill(''))

    const parts = fen.split(' ')
    const boardPart = parts[0]
    const ranks = boardPart.split('/')

    const board = []
    for (let i = 0; i < 8; i++) {
      const rank = []
      const rankStr = ranks[i]

      for (let j = 0; j < rankStr.length; j++) {
        const char = rankStr[j]
        if (char >= '1' && char <= '8') {
          for (let k = 0; k < parseInt(char); k++) {
            rank.push('')
          }
        } else {
          rank.push(char)
        }
      }
      board.push(rank)
    }
    return board
  }, [])

  function applyOptimisticMove(move) {
    const currentBoard = parseFEN(currentGame.board)
    const fromCoords = squareToCoords(move.from)
    const finalSquare = move.teleport || move.to
    const toCoords = squareToCoords(finalSquare)

    const newBoard = currentBoard.map(rank => [...rank])
    const piece = newBoard[fromCoords[0]][fromCoords[1]]
    const isWhitePiece = piece === piece.toUpperCase()

    newBoard[fromCoords[0]][fromCoords[1]] = ''
    newBoard[toCoords[0]][toCoords[1]] = move.promotion || piece

    if (piece.toLowerCase() === 'k' && Math.abs(fromCoords[1] - toCoords[1]) === 2) {
      const isKingSide = toCoords[1] === 6
      const rookFromFile = isKingSide ? 7 : 0
      const rookToFile = isKingSide ? 5 : 3
      const rank = fromCoords[0]
      const rook = newBoard[rank][rookFromFile]
      newBoard[rank][rookFromFile] = ''
      newBoard[rank][rookToFile] = rook
    }

    if (piece.toLowerCase() === 'p' && fromCoords[1] !== toCoords[1] && !currentBoard[toCoords[0]][toCoords[1]]) {
      const capturedPawnRank = isWhitePiece ? toCoords[0] + 1 : toCoords[0] - 1
      newBoard[capturedPawnRank][toCoords[1]] = ''
    }

    const newFEN = boardToFEN(newBoard, currentGame)

    return {
      ...currentGame,
      board: newFEN,
      current_turn: currentGame.current_turn === 'white' ? 'black' : 'white',
      move_count: currentGame.move_count + 1,
      red_squares: '[]'
    }
  }

  const parseSpecialSquares = useCallback((squaresJson) => {
    if (!squaresJson) return []
    try {
      return JSON.parse(squaresJson)
    } catch (err) {
      return []
    }
  }, [])

  const parseVisualEffects = useCallback((effectsJson) => {
    if (!effectsJson) return []
    try {
      return JSON.parse(effectsJson)
    } catch (err) {
      return []
    }
  }, [])

  const greenSquares = useMemo(
    () => parseSpecialSquares(currentGame?.green_squares),
    [currentGame?.green_squares, parseSpecialSquares]
  )

  const blueSquares = useMemo(
    () => parseSpecialSquares(currentGame?.blue_squares),
    [currentGame?.blue_squares, parseSpecialSquares]
  )

  const redSquares = useMemo(
    () => parseSpecialSquares(currentGame?.red_squares),
    [currentGame?.red_squares, parseSpecialSquares]
  )

  const visualEffects = useMemo(
    () => parseVisualEffects(currentGame?.visual_effects),
    [currentGame?.visual_effects, parseVisualEffects]
  )

  const playerColor = useMemo(() => {
    if (!currentGame || !user) return null
    if (currentGame.white_player_id === user.id) return 'white'
    if (currentGame.black_player_id === user.id) return 'black'
    return null
  }, [currentGame, user])

  useEffect(() => {
    if (playerColor) {
      setBoardOrientation(playerColor === 'black' ? 'black' : 'white')
    }
  }, [playerColor])

  useEffect(() => {
    if (!currentGame || (currentGame.status !== 'active' && currentGame.status !== 'pregame')) {
      return undefined
    }

    setNowTick(Date.now())
    const interval = setInterval(() => setNowTick(Date.now()), 1000)
    return () => clearInterval(interval)
  }, [currentGame?.id, currentGame?.status])

  useEffect(() => {
    if (currentGame?.status === 'completed' && currentGame?.result) {
      if (lastResultGameId !== currentGame.id) {
        setLastResultGameId(currentGame.id)
      }
    }
  }, [currentGame?.status, currentGame?.result, currentGame?.id, lastResultGameId])

  // Initialize rematch state when game changes
  useEffect(() => {
    if (currentGame && currentGame.status === 'completed') {
      // Check if there's already a rematch request
      if (currentGame.rematch_requested_by && !currentGame.rematch_accepted_by) {
        if (currentGame.rematch_requested_by === user?.id) {
          setRematchStatus('requested')
        } else {
          setRematchStatus('received')
          setRematchRequestedBy(currentGame.rematch_requested_by)
        }
      } else {
        setRematchStatus(null)
        setRematchRequestedBy(null)
      }
    } else {
      setRematchStatus(null)
      setRematchRequestedBy(null)
    }
  }, [currentGame?.id, currentGame?.status, currentGame?.rematch_requested_by, currentGame?.rematch_accepted_by, user?.id])

  const refreshCurrentGame = useCallback(async () => {
    if (!currentGame?.id) return null
    try {
      const updated = await getGame(currentGame.id)
      if (updated) {
        setCurrentGame(updated)
      }
      return updated || null
    } catch (err) {
      console.error('Failed to refresh game state:', err)
      return null
    }
  }, [currentGame?.id, getGame, setCurrentGame])


  // Helper functions for game evaluation - defined as regular functions to avoid initialization issues
  const calculateLegalMovesForTurn = (board, square, turn) => {
    const [rank, file] = squareToCoords(square)
    const piece = board[rank][file]
    if (!piece) return []
    const isWhitePiece = piece === piece.toUpperCase()
    if ((turn === 'white' && !isWhitePiece) || (turn === 'black' && isWhitePiece)) {
      return []
    }

    const legalMoves = []
    for (let toRank = 0; toRank < 8; toRank++) {
      for (let toFile = 0; toFile < 8; toFile++) {
        const toSquare = coordsToSquare(toRank, toFile)
        if (isValidMove(board, square, toSquare, turn)) {
          legalMoves.push(toSquare)
        }
      }
    }
    return legalMoves
  }

  const hasAnyLegalMovesForTurn = (game) => {
    if (!game?.board) return true
    const board = parseFEN(game.board)
    if (!board) return true
    const turn = game.current_turn

    for (let rank = 0; rank < 8; rank++) {
      for (let file = 0; file < 8; file++) {
        const piece = board[rank][file]
        if (!piece) continue
        const isWhitePiece = piece === piece.toUpperCase()
        if ((turn === 'white' && !isWhitePiece) || (turn === 'black' && isWhitePiece)) {
          continue
        }
        const square = coordsToSquare(rank, file)
        const legal = calculateLegalMovesForTurn(board, square, turn)
        if (legal.length > 0) {
          return true
        }
      }
    }

    return false
  }

  useEffect(() => {
    const evaluate = async () => {
      if (!currentGame || currentGame.status !== 'active') return

      // Only check for game end after a move has been made
      if (!currentGame.last_move_at) return

      const hasMoves = hasAnyLegalMovesForTurn(currentGame)
      if (!hasMoves && !evaluationPendingRef.current) {
        evaluationPendingRef.current = true
        try {
          await evaluateGame(currentGame.id)
        } catch (err) {
          // Silently handle expected errors (game not in completed state)
          if (!err.message.includes('not in a completed state')) {
            console.error('Failed to evaluate game state:', err)
          }
          evaluationPendingRef.current = false
        }
      }
    }
    evaluate()
  }, [currentGame, evaluateGame])

  const getPregameTimeLeft = useCallback(() => {
    if (!currentGame) return 0

    // Tournament Logic (10 minute grace period)
    if (currentGame.tournament_id) {
       const deadline = currentGame.tournament_game_start_deadline 
         ? new Date(currentGame.tournament_game_start_deadline).getTime()
         : new Date(currentGame.created_at).getTime() + (10 * 60 * 1000)
       
       const now = nowTick
       return Math.max(0, Math.floor((deadline - now) / 1000))
    }

    // Regular Game Logic (30s from pregame start)
    if (currentGame.status !== 'pregame' || !currentGame.pregame_start_at) {
      return 30
    }

    const pregameStartMs = new Date(currentGame.pregame_start_at).getTime()
    if (Number.isNaN(pregameStartMs)) {
      return 30
    }

    const elapsed = Math.max(0, Math.floor((nowTick - pregameStartMs) / 1000))
    return Math.max(0, 30 - elapsed)
  }, [currentGame?.status, currentGame?.pregame_start_at, currentGame?.tournament_id, currentGame?.tournament_game_start_deadline, currentGame?.created_at, nowTick])

  const pregameTimeLeft = useMemo(() => getPregameTimeLeft(), [getPregameTimeLeft])

  useEffect(() => {
    if (!currentGame || currentGame.status !== 'pregame') {
      pregameTimeoutTriggeredRef.current = false
      return
    }

    if (pregameTimeLeft <= 0 && !pregameTimeoutTriggeredRef.current) {
      pregameTimeoutTriggeredRef.current = true
      ;(async () => {
        const refreshed = await refreshCurrentGame()
        if (
          refreshed?.status === 'cancelled' &&
          !cancellationShownRef.current &&
          !showCancellationPopup
        ) {
          setCancellationMessage('Game was cancelled because white did not make the first move in time.')
          setShowCancellationPopup(true)
        }
        setTimeout(() => {
          pregameTimeoutTriggeredRef.current = false
        }, 2000)
      })()
    }
  }, [currentGame?.status, pregameTimeLeft, refreshCurrentGame, showCancellationPopup])

  useEffect(() => {
    if (
      currentGame?.status === 'cancelled' &&
      !showCancellationPopup &&
      !cancellationShownRef.current
    ) {
      setCancellationMessage('Game was cancelled because white did not make the first move in time.')
      setShowCancellationPopup(true)
    }
  }, [currentGame?.status, showCancellationPopup])

  useEffect(() => {
    if (showCancellationPopup) {
      cancellationShownRef.current = true
      const timeout = setTimeout(() => {
        setShowCancellationPopup(false)
        if (onBack) {
          onBack()
        }
      }, 2500)
      return () => clearTimeout(timeout)
    }
  }, [showCancellationPopup, onBack])

  const handleCheckIn = useCallback(async () => {
    if (!currentGame?.id) return
    try {
      await markReady(currentGame.id)
    } catch (err) {
      console.error("Check-in failed:", err)
    }
  }, [currentGame?.id, markReady])



  const getClockTime = useCallback((color) => {
    if (!currentGame) return 0

    const baseValue = color === 'white' ? currentGame.white_time : currentGame.black_time
    if (typeof baseValue !== 'number') return 0

    // Chess.com style: only countdown for current player's turn
    if (currentGame.current_turn !== color || currentGame.status !== 'active') {
      return Math.max(0, baseValue)
    }

    // Calculate elapsed time since last move for real-time countdown
    if (currentGame.last_move_time) {
      const lastMoveTime = new Date(currentGame.last_move_time).getTime()
      const now = Date.now()
      const elapsedSeconds = Math.floor((now - lastMoveTime) / 1000)
      return Math.max(0, baseValue - elapsedSeconds)
    }

    return Math.max(0, baseValue)
  }, [currentGame?.white_time, currentGame?.black_time, currentGame?.current_turn, currentGame?.last_move_time, currentGame?.status, nowTick])

  // Check if game ended due to timeout
  const isTimeoutEnd = useCallback(() => {
    if (!currentGame || currentGame.status !== 'completed') return false

    // Check if either player has 0 time remaining
    const whiteTime = currentGame.white_time || 0
    const blackTime = currentGame.black_time || 0

    return whiteTime === 0 || blackTime === 0
  }, [currentGame])

  // Get appropriate end game title
  const getGameEndTitle = useCallback(() => {
    if (isTimeoutEnd()) {
      return 'TIME EXPIRED'
    }

    // Use end_reason if available
    if (currentGame?.end_reason) {
      switch (currentGame.end_reason) {
        case 'resignation':
          const isPlayerWhite = currentGame.white_player_id === user?.id
          const isPlayerBlack = currentGame.black_player_id === user?.id
          if ((isPlayerWhite && currentGame.result === 'black_win') ||
              (isPlayerBlack && currentGame.result === 'white_win')) {
            return 'YOU RESIGNED'
          } else {
            return 'OPPONENT RESIGNED'
          }
        case 'timeout':
          return 'TIME EXPIRED'
        case 'repetition':
          return 'DRAW BY REPETITION'
        case 'stalemate':
          return 'STALEMATE'
        case 'insufficient_material':
          return 'INSUFFICIENT MATERIAL'
        case '50_move_rule':
          return 'DRAW BY 50-MOVE RULE'
        case 'abandoned':
          return 'GAME ABANDONED'
      }
    }

    // Default to checkmate for backwards compatibility
    return 'CHECKMATE'
  }, [isTimeoutEnd, currentGame?.end_reason, currentGame?.result, currentGame?.white_player_id, currentGame?.black_player_id, user?.id])

  // Frontend timeout detection as fallback - refreshes game state when timeout detected
  // Backend should handle timeouts, but this ensures the UI updates if WebSocket messages are delayed
  useEffect(() => {
    if (!currentGame || currentGame.status !== 'active') return

    const interval = setInterval(() => {
      const whiteTime = getClockTime('white')
      const blackTime = getClockTime('black')

      // If either player has run out of time, refresh game state to get the latest from backend
      if (whiteTime <= 0 || blackTime <= 0) {
        console.log('Frontend timeout detected - refreshing game state')
        refreshCurrentGame()
        clearInterval(interval)
      }
    }, 1000) // Check every second

    return () => clearInterval(interval)
  }, [currentGame, getClockTime, refreshCurrentGame])

  const formatClock = useCallback((seconds) => {
    const safeSeconds = Number.isFinite(seconds) ? Math.max(0, Math.floor(seconds)) : 0
    const mins = Math.floor(safeSeconds / 60)
    const secs = safeSeconds % 60
    return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`
  }, [])

  const getRatingDeltaForColor = useCallback((color) => {
    if (!currentGame || currentGame.status !== 'completed') return null

    // Check if game is ranked - only show rating changes for rated games
    if (!currentGame.rated) return null

    // Get rating values
    const before = color === 'white' ? currentGame.white_rating_before : currentGame.black_rating_before
    const after = color === 'white' ? currentGame.white_rating_after : currentGame.black_rating_after

    // Ensure we have valid rating numbers
    if (typeof before !== 'number' || typeof after !== 'number') {
      return null
    }

    const delta = after - before
    return delta
  }, [currentGame])

  const playerRatingDelta = useMemo(() => {
    if (!playerColor) return null
    return getRatingDeltaForColor(playerColor)
  }, [playerColor, getRatingDeltaForColor])

  // Calculate lost pieces by comparing current board with starting position
  const lostPieces = useMemo(() => {
    if (!currentGame?.board) {
      return { white: [], black: [] }
    }

    // Standard starting position pieces count
    const startingPieces = {
      'K': 1, 'Q': 1, 'R': 2, 'B': 2, 'N': 2, 'P': 8, // White pieces
      'k': 1, 'q': 1, 'r': 2, 'b': 2, 'n': 2, 'p': 8  // Black pieces
    }

    // Count current pieces on board
    const currentPieces = {
      'K': 0, 'Q': 0, 'R': 0, 'B': 0, 'N': 0, 'P': 0,
      'k': 0, 'q': 0, 'r': 0, 'b': 0, 'n': 0, 'p': 0
    }

    // Count promotions from move history
    let whitePromotionCount = 0
    let blackPromotionCount = 0

    if (currentGame?.moves && Array.isArray(currentGame.moves)) {
      currentGame.moves.forEach(move => {
        if (move.promotion) {
          const isWhiteMove = move.player_color === 'white' ||
                             (move.from && move.from[1] === '7' && move.to[1] === '8') ||
                             (move.from && move.from[1] === '2' && move.to[1] === '1')

          if (isWhiteMove) {
            whitePromotionCount++
          } else {
            blackPromotionCount++
          }
        }
      })
    }

    try {
      // Parse current board state (FEN format)
      const fenParts = currentGame.board.split(' ')
      const boardPart = fenParts[0] // Get the piece placement part

      // Count pieces on current board
      for (const char of boardPart) {
        if (currentPieces.hasOwnProperty(char)) {
          currentPieces[char]++
        }
      }

      // Calculate missing pieces
      const whiteLost = []
      const blackLost = []

      // Check each piece type
      for (const piece in startingPieces) {
        let missing = startingPieces[piece] - currentPieces[piece]

        // Adjust for promotions: subtract promoted pawns from missing pawn count
        if (piece === 'P') { // White pawns
          missing = Math.max(0, missing - whitePromotionCount)
        } else if (piece === 'p') { // Black pawns
          missing = Math.max(0, missing - blackPromotionCount)
        }

        for (let i = 0; i < missing; i++) {
          const isWhitePiece = piece === piece.toUpperCase()
          if (isWhitePiece) {
            whiteLost.push(piece.toLowerCase()) // White piece was lost
          } else {
            blackLost.push(piece) // Black piece was lost
          }
        }
      }

      return { white: whiteLost, black: blackLost }
    } catch (e) {
      return { white: [], black: [] }
    }
  }, [currentGame?.board, currentGame?.moves])

  // Calculate promotions by tracking actual promotions from move history
  const promotions = useMemo(() => {
    if (!currentGame?.moves || !Array.isArray(currentGame.moves)) {
      return { white: [], black: [] }
    }

    const whitePromotions = []
    const blackPromotions = []

    try {
      // Go through all moves and find ones with promotion
      currentGame.moves.forEach((move, index) => {
        if (move.promotion) {
          // Determine player color based on move player info or rank destination
          const isWhiteMove = move.player_color === 'white' ||
                             (move.from && move.to && move.from[1] === '7' && move.to[1] === '8')
          const isBlackMove = move.player_color === 'black' ||
                             (move.from && move.to && move.from[1] === '2' && move.to[1] === '1')

          if (isWhiteMove) {
            whitePromotions.push(move.promotion.toLowerCase())
          } else if (isBlackMove) {
            blackPromotions.push(move.promotion.toLowerCase())
          }
        }
      })

      return { white: whitePromotions, black: blackPromotions }
    } catch (e) {
      return { white: [], black: [] }
    }
  }, [currentGame?.moves])

  // Helper function to group pieces and show counts for space efficiency
  const groupPieces = useCallback((pieces) => {
    const grouped = {}
    pieces.forEach(piece => {
      grouped[piece] = (grouped[piece] || 0) + 1
    })

    return Object.entries(grouped).map(([piece, count]) => ({
      piece,
      count,
      symbol: PIECE_SYMBOLS[piece] || PIECE_SYMBOLS[piece.toUpperCase()] || '?'
    }))
  }, [])

  const formatRatingDelta = useCallback((delta) => {
    if (delta === null || delta === undefined) return '—'
    if (delta > 0) return `+${delta}`
    if (delta < 0) return `${delta}`
    return '0'
  }, [])

  const getTeleportOptions = useCallback((move) => {
    if (!currentGame || !move?.to) return []
    const isBlueSquare = blueSquares?.some(square => square.position === move.to)
    if (!isBlueSquare) return []

    const board = parseFEN(currentGame.board)

    // Get the piece being teleported
    const [fromRank, fromFile] = squareToCoords(move.from)
    const piece = board[fromRank][fromFile]
    const isPawn = piece?.toLowerCase() === 'p'

    const options = []
    for (let rank = 0; rank < 8; rank++) {
      for (let file = 0; file < 8; file++) {
        if (board[rank][file] !== '') continue

        // If it's a pawn, exclude rank 0 and rank 7 (ranks 1 and 8 in chess notation)
        if (isPawn && (rank === 0 || rank === 7)) continue

        const square = coordsToSquare(rank, file)
        const onRed = redSquares?.some(sq => sq.position === square)
        const onGreen = greenSquares?.some(sq => sq.position === square)
        // Allow teleporting to blue squares (including staying on the current blue square)
        // but not to red or green squares
        if (onRed || onGreen) continue
        options.push(square)
      }
    }
    return options.sort()
  }, [currentGame, blueSquares, redSquares, greenSquares, parseFEN, coordsToSquare, squareToCoords])

  const getIllegalTeleportSquares = useCallback((move) => {
    if (!currentGame || !move?.to) return []
    const isBlueSquare = blueSquares?.some(square => square.position === move.to)
    if (!isBlueSquare) return []

    const board = parseFEN(currentGame.board)

    // Get the piece being teleported
    const [fromRank, fromFile] = squareToCoords(move.from)
    const piece = board[fromRank][fromFile]
    const isPawn = piece?.toLowerCase() === 'p'

    // Only show illegal squares for pawns
    if (!isPawn) return []

    const illegalSquares = []
    // Check ranks 1 and 8 (rank 0 and 7 in 0-indexed coordinates)
    for (let rank of [0, 7]) {
      for (let file = 0; file < 8; file++) {
        // Only include empty squares
        if (board[rank][file] !== '') continue

        const square = coordsToSquare(rank, file)
        const onRed = redSquares?.some(sq => sq.position === square)
        const onGreen = greenSquares?.some(sq => sq.position === square)
        const onBlue = blueSquares?.some(sq => sq.position === square)
        // Don't show special squares as illegal since they're already blocked
        if (onRed || onGreen || onBlue) continue

        illegalSquares.push(square)
      }
    }
    return illegalSquares.sort()
  }, [currentGame, blueSquares, redSquares, greenSquares, parseFEN, coordsToSquare, squareToCoords])

  const teleportGhostPiece = useMemo(() => {
    if (!teleportPrompt?.move || !currentGame?.board) return null
    const board = parseFEN(currentGame.board)
    const [rank, file] = squareToCoords(teleportPrompt.move.from)
    return board?.[rank]?.[file] || null
  }, [teleportPrompt, currentGame?.board])

  const illegalTeleportSquares = useMemo(() => {
    if (!teleportPrompt?.move) return []
    return getIllegalTeleportSquares(teleportPrompt.move)
  }, [teleportPrompt?.move, getIllegalTeleportSquares])

  const buildPromotionOptions = useCallback((pieces, isWhite) => {
    return pieces.map(symbol => {
      const label = PROMOTION_LABELS[symbol] || symbol
      const value = isWhite ? symbol : symbol.toLowerCase()
      const pieceKey = isWhite ? symbol : symbol.toLowerCase()
      const pieceImage = pieceImages[pieceKey] || null
      return { label, value, image: pieceImage }
    })
  }, [pieceImages])

  const pendingPromotionEffect = useMemo(() => {
    if (!visualEffects || !playerColor) return null
    return visualEffects.find(effect => {
      if (effect.type !== 'promotion_pending') return false
      const color = (effect.player_color || (effect.piece === effect.piece?.toUpperCase() ? 'white' : 'black')).toLowerCase()
      return color === playerColor
    })
  }, [visualEffects, playerColor])

  useEffect(() => {
    if (!pendingPromotionEffect) {
      if (promotionPrompt?.type === 'pending') {
        setPromotionPrompt(null)
      }
      return
    }

    if (promotionPrompt?.type === 'pending') {
      return
    }

    const isWhite = (pendingPromotionEffect.player_color || '').toLowerCase() !== 'black'
    setPromotionPrompt({
      type: 'pending',
      position: pendingPromotionEffect.position,
      options: buildPromotionOptions(['Q', 'R', 'B', 'N'], isWhite),
      message: 'Your teleported pawn reached the final rank. Choose a promotion to continue.'
    })
  }, [pendingPromotionEffect, promotionPrompt, buildPromotionOptions])

  const evaluatePromotionContext = useCallback((move) => {
    if (!currentGame) {
      return { needsChoice: false, autoPromotion: null, options: [], message: '' }
    }

    const board = parseFEN(currentGame.board)
    const [fromRank, fromFile] = squareToCoords(move.from)
    const piece = board?.[fromRank]?.[fromFile]

    if (!piece) {
      return { needsChoice: false, autoPromotion: null, options: [], message: '' }
    }

    const isWhite = piece === piece.toUpperCase()
    const pieceType = piece.toLowerCase()
    const [toRank] = squareToCoords(move.to)
    const finalRank = isWhite ? 0 : 7

    const onGreenSquare = !!greenSquares?.find(square => square.position === move.to)

    if (onGreenSquare) {
      if (pieceType === 'p') {
        return {
          needsChoice: true,
          autoPromotion: null,
          options: buildPromotionOptions(['B', 'N'], isWhite),
          message: 'Choose how to empower your pawn on the green square.'
        }
      }

      if (pieceType === 'b' || pieceType === 'n') {
        return {
          needsChoice: false,
          autoPromotion: isWhite ? 'R' : 'r',
          options: [],
          message: ''
        }
      }

      if (pieceType === 'r') {
        return {
          needsChoice: false,
          autoPromotion: isWhite ? 'Q' : 'q',
          options: [],
          message: ''
        }
      }
    }

    if (pieceType === 'p' && toRank === finalRank) {
      return {
        needsChoice: true,
        autoPromotion: null,
        options: buildPromotionOptions(['Q', 'R', 'B', 'N'], isWhite),
        message: 'Your pawn reached the final rank! Pick a promotion.'
      }
    }

    return { needsChoice: false, autoPromotion: null, options: [], message: '' }
  }, [currentGame, greenSquares, buildPromotionOptions])

  // Helper function to detect if a move is a capture
  const isMoveCapture = useCallback((move, boardState) => {
    if (!move || !boardState) return false

    try {
      const board = parseFEN(boardState)
      const [toRank, toFile] = squareToCoords(move.to)
      const targetPiece = board[toRank][toFile]

      // A move is a capture if there's a piece at the destination square
      return targetPiece !== ''
    } catch (error) {
      console.error('Error checking if move is capture:', error)
      return false
    }
  }, [])

  // Check if a move should generate red squares (every 3rd move: 3, 6, 9, 12, etc.)
  const shouldGenerateRedSquares = useCallback((currentMoveCount) => {
    const nextMoveCount = currentMoveCount + 1
    return nextMoveCount > 0 && nextMoveCount % 3 === 0
  }, [])

  // Get the piece from the board at a specific position
  const getPieceAtPosition = useCallback((position, boardFEN) => {
    try {
      const board = parseFEN(boardFEN)
      const [rank, file] = squareToCoords(position)
      return board[rank][file] || ''
    } catch (error) {
      return ''
    }
  }, [parseFEN, squareToCoords])

  const executeMove = useCallback((movePayload) => {
    console.log('[GAME] Attempting move', {
      move: movePayload,
      gameId: currentGame?.id,
      currentTurn: currentGame?.current_turn,
      playerIds: {
        white: currentGame?.white_player_id,
        black: currentGame?.black_player_id,
        user: user?.id
      },
      isPlayerTurn,
      isConnected,
      connectionStatus
    })

    if (!currentGame || !isPlayerTurn || !isConnected) {
      console.warn('[GAME] Move blocked', {
        reason: !currentGame ? 'no_game' : (!isPlayerTurn ? 'not_player_turn' : 'not_connected'),
        gameId: currentGame?.id
      })
      return
    }

    setMoveError(null)
    console.log('[GAME] Sending move', {
      move: movePayload,
      gameId: currentGame?.id,
      isConnected,
      socketState: connectionStatus
    })

    // Get piece that's moving for animation
    const movingPiece = getPieceAtPosition(movePayload.from, currentGame.board)
    const isCapture = isMoveCapture(movePayload, currentGame.board)
    const willGenerateRedSquares = shouldGenerateRedSquares(currentGame.move_count)

    // Apply move optimistically for immediate visual feedback (no animation delay for user moves)
    const optimisticState = applyOptimisticMove(movePayload)
    setOptimisticGame(optimisticState)
    setPendingMove(movePayload)
    pendingMoveRef.current = movePayload

    // Send move via WebSocket for server validation
    const success = sendMove(currentGame.id, movePayload)

    if (!success) {
      console.warn('[GAME] sendMove() returned false - socket not open', {
        move: movePayload,
        gameId: currentGame?.id
      })
      // Rollback optimistic update if send failed
      clearOptimisticState()
      setMoveError('Failed to send move - WebSocket not connected')
    } else {
      // Play appropriate sound when move is successfully sent
      soundManager.playMoveOrCapture(isCapture)

      // Set waiting state if red squares expected
      if (willGenerateRedSquares) {
        setWaitingForRedSquares(true)
      }

      // Set a timeout fallback to clear optimistic state if no response comes
      if (moveTimeoutRef.current) {
        clearTimeout(moveTimeoutRef.current)
      }
      moveTimeoutRef.current = setTimeout(() => {
        const pending = pendingMoveRef.current
        if (
          pending &&
          pending.from === movePayload.from &&
          pending.to === movePayload.to &&
          pending.promotion === movePayload.promotion
        ) {
          ;(async () => {
            const pendingMoveBeforeSync = pendingMoveRef.current
            const reconnectGameId = currentGame?.id
            console.warn('[GAME] Move sync timeout without confirmation. Refreshing + reconnect.', {
              move: pendingMoveBeforeSync,
              gameId: reconnectGameId
            })
            await refreshCurrentGame()
            clearOptimisticState()
            setMoveError('Synchronizing move with server...')
            setWaitingForRedSquares(false)
            if (pendingMoveBeforeSync) {
              console.warn('[GAME] Pending move still exists post-refresh, forcing socket reconnect')
              connect?.()
              if (reconnectGameId && joinGame) {
                console.warn('[GAME] Rejoining game room after timeout', reconnectGameId)
                joinGame(reconnectGameId)
              }
            }
          })()
        }
        moveTimeoutRef.current = null
      }, willGenerateRedSquares ? 8000 : 5000) // Longer timeout for red square moves
    }

  }, [currentGame, isPlayerTurn, isConnected, connectionStatus, applyOptimisticMove, sendMove, clearOptimisticState, refreshCurrentGame,
      shouldGenerateRedSquares, getPieceAtPosition, isMoveCapture, connect, joinGame, setMoveError, setWaitingForRedSquares])

  const handlePromotionSelection = async (value) => {
    if (!promotionPrompt) {
      setPromotionPrompt(null)
      return
    }

    if (promotionPrompt.type === 'pending') {
      try {
        await choosePromotion(currentGame.id, {
          position: promotionPrompt.position,
          promotion: value
        })
        setPromotionPrompt(null)
      } catch (err) {
        setMoveError(err.message || 'Failed to apply promotion')
      }
      return
    }

    if (!promotionPrompt.move) {
      setPromotionPrompt(null)
      return
    }

    const moveWithPromotion = value ? { ...promotionPrompt.move, promotion: value } : promotionPrompt.move
    setPromotionPrompt(null)
    executeMove(moveWithPromotion)
  }

  const handlePromotionCancel = () => {
    if (promotionPrompt?.type === 'pending') {
      return
    }
    setPromotionPrompt(null)
  }

  const handleTeleportSelection = (destination) => {
    if (!teleportPrompt) return
    const moveWithTeleport = { ...teleportPrompt.move, teleport: destination }
    setMoveError(null)
    setTeleportPrompt(null)
    handleMove(moveWithTeleport)
  }

  const getResultMessage = useCallback(() => {
    if (!currentGame?.result) return 'Game finished'

    const whiteTime = typeof currentGame.white_time === 'number' ? currentGame.white_time : null
    const blackTime = typeof currentGame.black_time === 'number' ? currentGame.black_time : null
    const whiteFlagged = whiteTime !== null && whiteTime <= 0
    const blackFlagged = blackTime !== null && blackTime <= 0

    switch (currentGame.result) {
      case 'white_win':
        if (blackFlagged && !whiteFlagged) {
          return 'White wins on time.'
        }
        return 'White wins!'
      case 'black_win':
        if (whiteFlagged && !blackFlagged) {
          return 'Black wins on time.'
        }
        return 'Black wins!'
      case 'draw':
        return 'The game is a draw (stalemate).'
      default:
        return 'Game completed.'
    }
  }, [currentGame?.result])


  // Validate if a move is legal (client-side validation)
  const isValidMove = (board, fromSquare, toSquare, turn) => {
    const fromCoords = squareToCoords(fromSquare)
    const toCoords = squareToCoords(toSquare)

    const piece = board[fromCoords[0]][fromCoords[1]]
    if (!piece) return false

    const isWhitePiece = piece === piece.toUpperCase()
    const isPlayerWhite = currentGame?.white_player_id === user?.id

    // Can only move own pieces
    if ((isPlayerWhite && !isWhitePiece) || (!isPlayerWhite && isWhitePiece)) {
      return false
    }

    // Can't capture own piece
    const targetPiece = board[toCoords[0]][toCoords[1]]
    if (targetPiece) {
      const targetIsWhite = targetPiece === targetPiece.toUpperCase()
      if (isWhitePiece === targetIsWhite) return false
    }

    const pieceType = piece.toLowerCase()
    const [fromRank, fromFile] = fromCoords
    const [toRank, toFile] = toCoords

    // Piece-specific movement validation
    let isValidPieceMove = false
    switch (pieceType) {
      case 'p': // Pawn
        isValidPieceMove = isValidPawnMove(board, fromRank, fromFile, toRank, toFile, isWhitePiece)
        break
      case 'r': // Rook
        isValidPieceMove = isValidRookMove(board, fromRank, fromFile, toRank, toFile)
        break
      case 'n': // Knight
        isValidPieceMove = isValidKnightMove(fromRank, fromFile, toRank, toFile)
        break
      case 'b': // Bishop
        isValidPieceMove = isValidBishopMove(board, fromRank, fromFile, toRank, toFile)
        break
      case 'q': // Queen
        isValidPieceMove = isValidQueenMove(board, fromRank, fromFile, toRank, toFile)
        break
      case 'k': // King
        isValidPieceMove = isValidKingMove(board, fromRank, fromFile, toRank, toFile, isWhitePiece)
        break
      default:
        return false
    }

    // If the basic piece move is invalid, reject
    if (!isValidPieceMove) return false

    // Check if the move would leave the king in check
    if (wouldMoveLeaveKingInCheck(board, fromRank, fromFile, toRank, toFile)) {
      return false
    }

    return true
  }

  // Chess piece movement validation functions
  const isValidPawnMove = (board, fromRank, fromFile, toRank, toFile, isWhite) => {
    const direction = isWhite ? -1 : 1
    const startRank = isWhite ? 6 : 1

    // Forward move
    if (fromFile === toFile) {
      // One square forward
      if (toRank === fromRank + direction && !board[toRank][toFile]) {
        return true
      }
      // Two squares from starting position
      if (
        fromRank === startRank &&
        toRank === fromRank + 2 * direction &&
        !board[toRank][toFile] &&
        !board[fromRank + direction][toFile]
      ) {
        return true
      }
    }

    // Diagonal capture
    if (Math.abs(fromFile - toFile) === 1 && toRank === fromRank + direction) {
      const targetPiece = board[toRank][toFile]
      if (targetPiece) {
        const targetIsWhite = targetPiece === targetPiece.toUpperCase()
        return isWhite !== targetIsWhite
      }

      // En passant capture
      if (!targetPiece && isEnPassantCapture(fromRank, fromFile, toRank, toFile, isWhite)) {
        return true
      }
    }

    return false
  }

  // Check if move is en passant capture
  const isEnPassantCapture = (fromRank, fromFile, toRank, toFile, isWhite) => {
    const expectedRank = isWhite ? 3 : 4 // 5th rank for white, 4th rank for black

    if (fromRank !== expectedRank) return false

    // Check if there's an enemy pawn on the same rank that just moved two squares
    const board = parseFEN(currentGame.board)
    const targetFile = toFile
    const enemyPawn = board[fromRank][targetFile]

    if (!enemyPawn) return false

    const enemyIsWhite = enemyPawn === enemyPawn.toUpperCase()
    if (enemyIsWhite === isWhite) return false // Must be enemy pawn

    if (enemyPawn.toLowerCase() !== 'p') return false // Must be a pawn

    // In a real implementation, we'd check the last move from game history
    // For now, we'll allow en passant if the conditions are met
    // This should be validated on the server side with proper game history
    return true
  }

  const isValidRookMove = (board, fromRank, fromFile, toRank, toFile) => {
    // Must move in straight line
    if (fromRank !== toRank && fromFile !== toFile) return false

    return isPathClear(board, fromRank, fromFile, toRank, toFile)
  }

  const isValidKnightMove = (fromRank, fromFile, toRank, toFile) => {
    const rankDiff = Math.abs(toRank - fromRank)
    const fileDiff = Math.abs(toFile - fromFile)
    return (rankDiff === 2 && fileDiff === 1) || (rankDiff === 1 && fileDiff === 2)
  }

  const isValidBishopMove = (board, fromRank, fromFile, toRank, toFile) => {
    // Must move diagonally
    if (Math.abs(toRank - fromRank) !== Math.abs(toFile - fromFile)) return false

    return isPathClear(board, fromRank, fromFile, toRank, toFile)
  }

  const isValidQueenMove = (board, fromRank, fromFile, toRank, toFile) => {
    return isValidRookMove(board, fromRank, fromFile, toRank, toFile) ||
           isValidBishopMove(board, fromRank, fromFile, toRank, toFile)
  }

  const isValidKingMove = (board, fromRank, fromFile, toRank, toFile, isWhite) => {
    const rankDiff = Math.abs(toRank - fromRank)
    const fileDiff = Math.abs(toFile - fromFile)

    // Regular king move (one square in any direction)
    if (rankDiff <= 1 && fileDiff <= 1) {
      return true
    }

    // Castling
    if (rankDiff === 0 && fileDiff === 2) {
      return isCastlingMove(board, fromRank, fromFile, toRank, toFile, isWhite)
    }

    return false
  }

  // Check if move is a valid castling move
  const isCastlingMove = (board, fromRank, fromFile, toRank, toFile, isWhite) => {
    const expectedRank = isWhite ? 7 : 0 // 1st rank for white, 8th rank for black

    // King must be on starting rank
    if (fromRank !== expectedRank) return false

    // King must be on starting file (e-file)
    if (fromFile !== 4) return false

    const isKingSide = toFile === 6 // g-file
    const isQueenSide = toFile === 2 // c-file

    if (!isKingSide && !isQueenSide) return false

    // Check if rook is in correct position
    const rookFile = isKingSide ? 7 : 0 // h-file or a-file
    const rook = board[expectedRank][rookFile]

    if (!rook) return false

    const rookIsWhite = rook === rook.toUpperCase()
    if (rookIsWhite !== isWhite) return false

    if (rook.toLowerCase() !== 'r') return false

    // Check if path is clear between king and rook
    const startFile = Math.min(fromFile, rookFile)
    const endFile = Math.max(fromFile, rookFile)

    for (let file = startFile + 1; file < endFile; file++) {
      if (board[expectedRank][file]) return false
    }

    // Prevent castling while in check
    if (isKingInCheck(board, isWhite)) {
      return false
    }

    // Ensure king does not pass through or land on attacked squares
    const direction = isKingSide ? 1 : -1
    let currentFile = fromFile
    for (let step = 0; step < 2; step++) {
      currentFile += direction
      if (isSquareUnderAttack(board, fromRank, currentFile, !isWhite)) {
        return false
      }
    }

    return true
  }

  const isPathClear = (board, fromRank, fromFile, toRank, toFile) => {
    const rankStep = toRank > fromRank ? 1 : toRank < fromRank ? -1 : 0
    const fileStep = toFile > fromFile ? 1 : toFile < fromFile ? -1 : 0

    let currentRank = fromRank + rankStep
    let currentFile = fromFile + fileStep

    while (currentRank !== toRank || currentFile !== toFile) {
      if (board[currentRank][currentFile]) return false
      currentRank += rankStep
      currentFile += fileStep
    }

    return true
  }

  // Check detection functions
  const isKingInCheck = (board, isWhite) => {
    // Find the king's position
    let kingRank = -1, kingFile = -1
    const kingChar = isWhite ? 'K' : 'k'

    for (let rank = 0; rank < 8; rank++) {
      for (let file = 0; file < 8; file++) {
        if (board[rank][file] === kingChar) {
          kingRank = rank
          kingFile = file
          break
        }
      }
      if (kingRank !== -1) break
    }

    if (kingRank === -1) return false // King not found

    // Check if any opponent piece can attack the king's position
    return isSquareUnderAttack(board, kingRank, kingFile, !isWhite)
  }

  const isSquareUnderAttack = (board, targetRank, targetFile, byWhite) => {
    // Check all squares on the board for attacking pieces
    for (let rank = 0; rank < 8; rank++) {
      for (let file = 0; file < 8; file++) {
        const piece = board[rank][file]
        if (!piece) continue

        // Check if this piece belongs to the attacking color
        const isPieceWhite = piece === piece.toUpperCase()
        if (isPieceWhite !== byWhite) continue

        // Check if this piece can attack the target square
        if (canPieceAttackSquare(board, rank, file, targetRank, targetFile)) {
          return true
        }
      }
    }
    return false
  }

  const canPieceAttackSquare = (board, fromRank, fromFile, toRank, toFile) => {
    const piece = board[fromRank][fromFile]
    if (!piece) return false

    const pieceType = piece.toLowerCase()
    const isWhite = piece === piece.toUpperCase()

    switch (pieceType) {
      case 'p':
        return canPawnAttackSquare(fromRank, fromFile, toRank, toFile, isWhite)
      case 'r':
        return isValidRookMove(board, fromRank, fromFile, toRank, toFile)
      case 'n':
        return isValidKnightMove(fromRank, fromFile, toRank, toFile)
      case 'b':
        return isValidBishopMove(board, fromRank, fromFile, toRank, toFile)
      case 'q':
        return isValidQueenMove(board, fromRank, fromFile, toRank, toFile)
      case 'k':
        return canKingAttackSquare(fromRank, fromFile, toRank, toFile)
      default:
        return false
    }
  }


  const canPawnAttackSquare = (fromRank, fromFile, toRank, toFile, isWhite) => {
    const fileDiff = Math.abs(fromFile - toFile)
    const rankDiff = toRank - fromRank

    // Pawn attacks diagonally
    if (fileDiff !== 1) return false

    if (isWhite) {
      // White pawns attack "up" the board (decreasing rank)
      return rankDiff === -1
    } else {
      // Black pawns attack "down" the board (increasing rank)
      return rankDiff === 1
    }
  }

  const canKingAttackSquare = (fromRank, fromFile, toRank, toFile) => {
    const rankDiff = Math.abs(fromRank - toRank)
    const fileDiff = Math.abs(fromFile - toFile)

    // King attacks one square in any direction
    return rankDiff <= 1 && fileDiff <= 1 && !(rankDiff === 0 && fileDiff === 0)
  }

  const wouldMoveLeaveKingInCheck = (board, fromRank, fromFile, toRank, toFile) => {
    // Make a copy of the board and apply the move
    const boardCopy = board.map(rank => [...rank])
    const piece = boardCopy[fromRank][fromFile]

    if (!piece) return false

    const isWhite = piece === piece.toUpperCase()

    // Apply the move to the copy
    boardCopy[toRank][toFile] = piece
    boardCopy[fromRank][fromFile] = ''

    // Check if the king is in check after this move
    return isKingInCheck(boardCopy, isWhite)
  }

  const wouldTeleportLeaveKingInCheck = (board, fromRank, fromFile, toRank, toFile) => {
    // Make a copy of the board and apply the teleport
    const boardCopy = board.map(rank => [...rank])
    const piece = boardCopy[fromRank][fromFile]

    if (!piece) return false

    const isWhite = piece === piece.toUpperCase()

    // Apply the teleport to the copy (move piece to destination)
    boardCopy[toRank][toFile] = piece
    boardCopy[fromRank][fromFile] = ''

    // Check if the king is in check after this teleport
    return isKingInCheck(boardCopy, isWhite)
  }

  // Handle square click (for piece selection and movement)
  const handleSquareClick = (square, rank, file) => {
    if (teleportPrompt) {
      if (teleportPrompt.options.includes(square)) {
        handleTeleportSelection(square)
      } else {
        setMoveError('Select one of the highlighted squares to complete the teleport.')
      }
      return
    }

    setMoveError(null)


    if (!selectedSquare) {
      // Select piece
      const board = parseFEN(currentGame.board)
      const piece = board[rank][file]

      if (piece && isPieceOwnedByPlayer(piece)) {
        setSelectedSquare(square)
        // Calculate legal moves for the selected piece
        const legal = calculateLegalMoves(board, square)
        setLegalMoves(legal)
      }
    } else {
      // Attempt move or premove
      if (selectedSquare === square) {
        // Clicked same square - deselect
        setSelectedSquare(null)
        setLegalMoves([])
      } else {
        // Only allow moves when it's the player's turn
        if (isPlayerTurn) {
          // Check if move is legal before attempting
          const board = parseFEN(currentGame.board)
          if (isValidMove(board, selectedSquare, square, currentGame.current_turn)) {
            handleMove({ from: selectedSquare, to: square })
          }
        }
        setSelectedSquare(null)
        setLegalMoves([])
      }
    }
  }
  // Handle move (from drag & drop or click)
  const handleMove = (move) => {

    if (teleportPrompt && !move.teleport) {
      setMoveError('Select a teleport destination to finish your previous move.')
      return
    }

    const isAndroid = /Android/i.test(navigator.userAgent);


    if (!currentGame || !isConnected) {
      return
    }

    // Only allow moves when it's the player's turn
    if (!isPlayerTurn) {
      return
    }

    const teleportOptions = getTeleportOptions(move)
    if (teleportOptions.length > 0 && !move.teleport) {
      setTeleportPrompt({ move, options: teleportOptions })
      return
    }

    if (currentGame.status !== 'active' && currentGame.status !== 'pregame') {
      setMoveError('Game is not active. Return to the dashboard to start a new one.')
      return
    }

    const promotionContext = evaluatePromotionContext(move)
    let preparedMove = { ...move }

    if (move.teleport) {
      preparedMove.teleport = move.teleport
    }

    if (promotionContext.autoPromotion) {
      preparedMove = { ...preparedMove, promotion: promotionContext.autoPromotion }
    }

    if (promotionContext.needsChoice) {
      setPromotionPrompt({
        type: 'pre',
        move: preparedMove,
        options: promotionContext.options,
        message: promotionContext.message
      })
      return
    }

    executeMove(preparedMove)
  }

  // Handle resignation
  const handleResign = async () => {
    if (!currentGame) return

    try {
      await resignGame(currentGame.id)
      await listGames()
      setShowResignModal(false)
      // Don't navigate away immediately - let WebSocket game_completed message show the popup
    } catch (err) {
      console.error('Failed to resign:', err)
    }
  }

  const handleCancelWaitingGame = useCallback(async () => {
    if (!currentGame?.id || isCancellingGame) return

    try {
      setMoveError(null)
      setIsCancellingGame(true)
      await deleteGame(currentGame.id)
      if (onBack) {
        onBack()
      } else {
        setCurrentGame(null)
      }
    } catch (err) {
      console.error('Failed to cancel waiting game:', err)
      setMoveError(err.message || 'Failed to cancel game')
    } finally {
      setIsCancellingGame(false)
    }
  }, [currentGame?.id, deleteGame, isCancellingGame, onBack, setCurrentGame])

  // Handle draw offer
  const handleOfferDraw = async () => {
    if (!currentGame) return

    try {
      await offerDraw(currentGame.id)
      setShowDrawModal(false)
      // Don't refresh here - let WebSocket broadcast handle the update
    } catch (err) {
      console.error('Failed to offer draw:', err)
      setMoveError(err.message || 'Failed to offer draw')
    }
  }

  // Handle cancel draw offer
  const handleCancelDraw = async () => {
    if (!currentGame) return

    try {
      await cancelDraw(currentGame.id)
      // Don't refresh here - let WebSocket broadcast handle the update
    } catch (err) {
      console.error('Failed to cancel draw offer:', err)
      setMoveError(err.message || 'Failed to cancel draw offer')
    }
  }

  const handleDrawResponse = async (action) => {
    if (!currentGame) return

    try {
      await respondDraw(currentGame.id, action)
      setShowIncomingDrawModal(false)
      // Don't refresh here - let WebSocket broadcast handle the update
    } catch (err) {
      console.error('Failed to respond to draw offer:', err)
      setMoveError(err.message || 'Failed to respond to draw offer')
    }
  }

  // Rematch functions
  const handleRequestRematch = () => {
    if (!currentGame || !sendMessage) return

    console.log('Requesting rematch for game:', currentGame.id)
    console.log('WebSocket connected:', isConnected)

    const success = sendMessage('rematch_request', null, currentGame.id)
    console.log('Rematch request sent successfully:', success)

    if (success) {
      setRematchStatus('requested')
    } else {
      console.error('Failed to send rematch request - WebSocket not connected')
    }
  }

  const handleRematchAction = (accept) => {
    if (!currentGame || !sendMessage) return

    sendMessage('rematch_response', { accept }, currentGame.id)
    setShowRematchModal(false)

    if (!accept) {
      setRematchStatus('declined')
      setRematchRequestedBy(null)
    }
  }

  const handleAcceptRematch = () => {
    handleRematchAction(true)
  }

  const handleDeclineRematch = () => {
    handleRematchAction(false)
  }

  // Check if piece belongs to current player
  const isPieceOwnedByPlayer = (piece) => {
    if (!piece || !user || !currentGame) return false

    const isWhitePiece = piece === piece.toUpperCase()
    const isPlayerWhite = currentGame.white_player_id === user.id

    return (isPlayerWhite && isWhitePiece) || (!isPlayerWhite && !isWhitePiece)
  }


  // Get player names
  const getPlayerName = (player) => {
    if (!player) return 'Waiting for player...'
    return player.username || 'Anonymous'
  }

  const getPlayerRating = (player) => {
    if (!player || !player.rating) return '(Unrated) '
    return `(${player.rating}) `
  }

  const pendingDrawOfferBy = currentGame?.pending_draw_offer_by
  const playerHasPendingDrawOffer = Boolean(
    pendingDrawOfferBy && user?.id && pendingDrawOfferBy === user.id
  )
  const opponentHasPendingDrawOffer = Boolean(
    pendingDrawOfferBy && user?.id && pendingDrawOfferBy !== user.id
  )
  const canOfferDraw = currentGame?.status === 'active' && !pendingDrawOfferBy

  const getOpponentPlayer = () => {
    if (!currentGame || !user) return null
    if (currentGame.white_player_id === user.id) {
      return currentGame.black_player
    }
    if (currentGame.black_player_id === user.id) {
      return currentGame.white_player
    }
    return null
  }

  const getOpponentDisplayName = () => {
    const opponent = getOpponentPlayer()
    return opponent ? getPlayerName(opponent) : 'Opponent'
  }

  // Get next square spawn preview
  const getNextSpawnPreview = () => {
    if (!currentGame) return <span className="spawn-none">No spawns predicted</span>

    const currentMove = currentGame.move_count || 0

    // RiftChess spawning pattern (example logic - adjust based on your actual rules)
    // Death squares spawn every 3 moves starting at move 3
    // Teleport squares spawn every 7 moves starting at move 7
    // Promotion squares spawn every 10 moves starting at move 10

    const nextDeathSpawn = Math.ceil((currentMove + 1) / 3) * 3
    const nextTeleportSpawn = Math.ceil((currentMove + 1) / 7) * 7
    const nextPromotionSpawn = Math.ceil((currentMove + 1) / 10) * 10

    const upcomingSpawns = [
      { type: 'death', move: nextDeathSpawn, color: 'red', name: 'Death' },
      { type: 'teleport', move: nextTeleportSpawn, color: 'blue', name: 'Teleport' },
      { type: 'promotion', move: nextPromotionSpawn, color: 'green', name: 'Promotion' }
    ].filter(spawn => spawn.move > currentMove)
      .sort((a, b) => a.move - b.move)

    if (upcomingSpawns.length === 0) {
      return <span className="spawn-none">No spawns predicted</span>
    }

    const nextSpawn = upcomingSpawns[0]
    const movesUntil = nextSpawn.move - currentMove

    return (
      <div className="spawn-prediction">
        <div className="spawn-item">
          <div className={`legend-color ${nextSpawn.color}`}></div>
          <span className="spawn-type">{nextSpawn.name}</span>
        </div>
        <div className="spawn-timing">
          <span className="spawn-move">Move {nextSpawn.move}</span>
          <span className="spawn-countdown">({movesUntil} {movesUntil === 1 ? 'move' : 'moves'} away)</span>
        </div>
      </div>
    )
  }

  // Get game status display
  const getGameStatus = () => {
    if (!currentGame) return 'Loading...'

    switch (currentGame.status) {
      case 'waiting':
        return 'Waiting for opponent'
      case 'pregame':
        const timeLeft = getPregameTimeLeft()
        return `Game starting in ${timeLeft}s...`
      case 'active':
        return isPlayerTurn ? 'Your turn' : "Opponent's turn"
      case 'completed':
        if (currentGame.result) {
          switch (currentGame.result) {
            case 'white_win':
              return 'White wins!'
            case 'black_win':
              return 'Black wins!'
            case 'draw':
              return 'Draw'
            default:
              return 'Game ended'
          }
        }
        return 'Game completed'
      default:
        return currentGame.status
    }
  }

  if (!currentGame) {
    return (
      <div className="game-loading">
        <div className="loading-spinner">Loading game...</div>
      </div>
    )
  }

  const handleRulesClick = (ruleType) => {
    setSelectedRule(ruleType)
    setShowRulesModal(true)
  }

  const closeRulesModal = () => {
    setShowRulesModal(false)
    setSelectedRule(null)
  }

  const getRuleContent = (ruleType) => {
    switch(ruleType) {
      case 'death':
        return {
          title: 'Death Squares',
          description: 'Red death squares will kill any piece that sits on them at the end of the turn. They spawn every 3 moves.'
        }
      case 'teleport':
        return {
          title: 'Teleport Squares',
          description: 'Blue teleport squares allow you to move your piece anywhere on the board where there is an empty square. Pawns cannot be teleported to ranks 1 or 8. They spawn every 7 moves.'
        }
      case 'promotion':
        return {
          title: 'Promotion Squares',
          description: 'Green promotion squares allow the player to promote a rook to a queen, a bishop or a knight to a rook and a pawn to a bishop or a knight. They spawn every 10 moves.'
        }
      default:
        return { title: '', description: '' }
    }
  }

  const isGameActive = currentGame.status === 'active' || currentGame.status === 'pregame'
  const whiteClock = getClockTime('white')
  const blackClock = getClockTime('black')

  return (
    <div className="game-container">
      <div className="game-layout-2col">
        {/* LEFT COLUMN: Game info and controls */}
        <div className="left-column">
          {currentGame.status === 'completed' ? (
            <div className="game-end-announcement">
              <div className={`announcement-title ${currentGame.result === 'draw' ? 'draw' : ''}`}>
                <h1>{currentGame.result === 'draw' ? 'DRAW' : getGameEndTitle()}</h1>
              </div>

              {currentGame.result !== 'draw' && (
                <div className="winner-announcement">
                  <h2>{getResultMessage()}</h2>
                </div>
              )}

              <div className="final-results">
                <div className={`result-player white-result ${currentGame.result === 'white_win' ? 'winner' : ''}`}>
                  <div className="result-color">⚪</div>
                  <div className="result-details">
                    <div className="result-name">
                      <UserDisplay user={currentGame.white_player} showTitleBadges={false} showStatusBadges={true} />
                    </div>
                    <div className="result-rating">
                      {currentGame.white_rating_after || currentGame.white_player?.rating || 1200}
                      <span className={`rating-change ${getRatingDeltaForColor('white') >= 0 ? 'positive' : 'negative'}`}>
                        {formatRatingDelta(getRatingDeltaForColor('white'))}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="result-vs">VS</div>

                <div className={`result-player black-result ${currentGame.result === 'black_win' ? 'winner' : ''}`}>
                  <div className="result-color">⚫</div>
                  <div className="result-details">
                    <div className="result-name">
                      <UserDisplay user={currentGame.black_player} showTitleBadges={false} showStatusBadges={true} />
                    </div>
                    <div className="result-rating">
                      {currentGame.black_rating_after || currentGame.black_player?.rating || 1200}
                      <span className={`rating-change ${getRatingDeltaForColor('black') >= 0 ? 'positive' : 'negative'}`}>
                        {formatRatingDelta(getRatingDeltaForColor('black'))}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* View Replay, Rematch, and Exit buttons */}
              <div style={{display: 'flex', flexDirection: 'column', gap: '1rem', marginTop: '1rem'}}>
                <button
                  className="replay-button"
                  onClick={() => {
                    if (onViewReplay && currentGame?.id) {
                      onViewReplay(currentGame.id)
                    } else {
                      console.log('View Replay clicked - onViewReplay not available')
                    }
                  }}
                >
                  View Replay
                </button>

                {/* Rematch functionality */}
                {((currentGame.white_player_id === user?.id) || (currentGame.black_player_id === user?.id)) && (
                  <>
                    {rematchStatus === null && (
                      <button
                        className="rematch-button"
                        onClick={handleRequestRematch}
                      >
                        Rematch
                      </button>
                    )}

                    {rematchStatus === 'requested' && (
                      <div className="rematch-status">Rematch requested...</div>
                    )}


                    {rematchStatus === 'declined' && (
                      <div className="rematch-status">Rematch declined</div>
                    )}
                  </>
                )}
                {onBack && (
                  <button className="back-button" onClick={onBack}>
                    Exit
                  </button>
                )}
              </div>
            </div>
          ) : (
            <>
              {/* Move tracker and game info - moved from right column */}

              <div className="move-tracker">
                <div className="move-tracker-header">
                  <span className="move-tracker-title">Move</span>
                  <span className="move-tracker-count">
                    {currentGame.move_count || 0}
                  </span>
                </div>
                <div className="move-tracker-game-info">
                  <span className="move-tracker-time-control">
                    {typeof currentGame.time_control === 'object'
                      ? `${Math.floor((currentGame.time_control.initial_time || 600) / 60)}+${currentGame.time_control.increment || 0}`
                      : (currentGame.time_control || '10+0')}
                  </span>
                  <span className="move-tracker-separator-dot">•</span>
                  <span className="move-tracker-ranked">
                    {currentGame.rated !== false ? 'Ranked' : 'Casual'}
                  </span>
                </div>

                {/* Session win tracking for rematch series */}
                {Object.keys(sessionWins).length > 0 && (
                  <div className="session-wins">
                    <div className="session-wins-header">Session Score</div>
                    <div className="session-wins-display">
                      <div className="session-player-score white">
                        <span className="player-name">
                          <UserDisplay user={currentGame.white_player} showTitleBadges={false} showStatusBadges={true} />
                        </span>
                        <span className="player-wins">
                          {sessionWins[currentGame.white_player_id] || 0}
                        </span>
                      </div>
                      <span className="session-separator">-</span>
                      <div className="session-player-score black">
                        <span className="player-wins">
                          {sessionWins[currentGame.black_player_id] || 0}
                        </span>
                        <span className="player-name">
                          <UserDisplay user={currentGame.black_player} showTitleBadges={false} showStatusBadges={true} />
                        </span>
                      </div>
                    </div>
                  </div>
                )}
                <div className="move-tracker-turn">
                  <span className={`turn-indicator ${currentGame.current_turn === 'white' ? 'white' : 'black'}`}>
                    {currentGame.current_turn === 'white' ? 'White to move' : 'Black to move'}
                  </span>
                </div>
                <div className="move-tracker-separator"></div>
              </div>



              {/* Teleport instruction */}
              {teleportPrompt && (
                <div className="teleport-instruction">
                  Teleporter active — click a highlighted square to choose your destination.
                </div>
              )}

              {/* Separator and action buttons */}

              {/* Draw offer status - in left column */}
              {currentGame.status === 'active' && pendingDrawOfferBy && (
                <div className={`draw-offer-status ${playerHasPendingDrawOffer ? 'waiting' : 'incoming'}`}>
                  {playerHasPendingDrawOffer
                    ? 'Waiting for opponent to respond to your draw offer...'
                    : 'Your opponent has offered a draw.'}
                </div>
              )}

              {/* Game actions in left column */}
              {currentGame.status === 'active' && (
                <div className="left-column-actions">
                  <div className="icon-buttons-row">
                    <button
                      className="flip-button"
                      onClick={() => setBoardOrientation(prev => prev === 'white' ? 'black' : 'white')}
                      title="⇅"
                    >
                      ⇅
                    </button>
                    {!playerHasPendingDrawOffer && (
                      <button
                        className="draw-button"
                        disabled={!canOfferDraw}
                        onClick={() => setShowDrawModal(true)}
                      >
                        ½
                      </button>
                    )}
                    {playerHasPendingDrawOffer && (
                      <button
                        className="cancel-draw-button"
                        onClick={handleCancelDraw}
                      >
                        ½
                      </button>
                    )}
                    <button
                      className="resign-button"
                      onClick={() => setShowResignModal(true)}
                    >
                      ✕
                    </button>
                  </div>
                  <button className="back-button" onClick={onBack}>
                    Back
                  </button>
                </div>
              )}

              {/* View Replay and Rematch buttons when game is completed */}
              {currentGame.status === 'completed' && (
                <>
                  <div className="left-column-actions">
                    <button
                      className="flip-button"
                      onClick={() => setBoardOrientation(prev => prev === 'white' ? 'black' : 'white')}
                      title="⇅"
                    >
                      ⇅
                    </button>
                    <button
                      className="replay-button"
                      onClick={() => {
                        if (onViewReplay && currentGame?.id) {
                          onViewReplay(currentGame.id)
                        } else {
                          console.log('View Replay clicked - onViewReplay not available')
                        }
                      }}
                    >
                      View Replay
                    </button>

                    {/* Rematch functionality in left column */}
                    {((currentGame.white_player_id === user?.id) || (currentGame.black_player_id === user?.id)) && (
                      <>
                        {rematchStatus === null && (
                          <button
                            className="rematch-button"
                            onClick={handleRequestRematch}
                          >
                            Rematch
                          </button>
                        )}

                        {rematchStatus === 'requested' && (
                          <div className="rematch-status-small">Rematch requested...</div>
                        )}


                        {rematchStatus === 'declined' && (
                          <div className="rematch-status-small">Rematch declined</div>
                        )}
                      </>
                    )}
                    <button className="back-button" onClick={onBack}>
                      Back
                    </button>
                  </div>
                </>
              )}
              {/* Pregame countdown timer */}
              {currentGame.status === 'pregame' && (
                <>
                  <div className="left-column-actions">
                    <button
                      className="flip-button"
                      onClick={() => setBoardOrientation(prev => prev === 'white' ? 'black' : 'white')}
                      title="⇅"
                    >
                      ⇅
                    </button>
                    <button className="back-button" onClick={onBack}>
                      Back
                    </button>
                  </div>
                      <div className="pregame-countdown">
                    <div className="countdown-timer">
                      <span className="countdown-number">{pregameTimeLeft}</span>
                      <span className="countdown-label">seconds</span>
                    </div>
                    <div className="countdown-message">White must move to start the game</div>
                  </div>
                </>
              )}

            </>
          )}

        </div>

        {/* CENTER COLUMN: Chess board */}
        <div className="center-column">
          {/* Mobile countdown timer - shown above board on mobile */}
          {currentGame.status === 'pregame' && (
            <div className="pregame-countdown mobile-countdown">
              <div className="countdown-timer">
                <span className="countdown-number">{pregameTimeLeft}</span>
                <span className="countdown-label">seconds</span>
              </div>
              <div className="countdown-message">White must move to start the game</div>
            </div>
          )}

          {/* Mobile endgame message - shown above board on mobile */}
          {currentGame.status === 'completed' && (
            <div className="mobile-game-end-announcement">
              <div className={`announcement-title ${currentGame.result === 'draw' ? 'draw' : ''}`}>
                <h1>{currentGame.result === 'draw' ? 'DRAW' : getGameEndTitle()}</h1>
              </div>

              {currentGame.result !== 'draw' && (
                <div className="winner-announcement">
                  <h2>{getResultMessage()}</h2>
                </div>
              )}

              <div className="mobile-results">
                <div className={`result-player white-result ${currentGame.result === 'white_win' ? 'winner' : ''}`}>
                  <div className="result-details">
                    <div className="result-name">
                      <UserDisplay user={currentGame.white_player} showTitleBadges={false} showStatusBadges={true} />
                    </div>
                    <div className="result-rating">
                      {currentGame.white_rating_after || currentGame.white_player?.rating || 1200}
                      <span className={`rating-change ${getRatingDeltaForColor('white') >= 0 ? 'positive' : 'negative'}`}>
                        {formatRatingDelta(getRatingDeltaForColor('white'))}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="result-vs">VS</div>

                <div className={`result-player black-result ${currentGame.result === 'black_win' ? 'winner' : ''}`}>
                  <div className="result-details">
                    <div className="result-name">
                      <UserDisplay user={currentGame.black_player} showTitleBadges={false} showStatusBadges={true} />
                    </div>
                    <div className="result-rating">
                      {currentGame.black_rating_after || currentGame.black_player?.rating || 1200}
                      <span className={`rating-change ${getRatingDeltaForColor('black') >= 0 ? 'positive' : 'negative'}`}>
                        {formatRatingDelta(getRatingDeltaForColor('black'))}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Waiting for player modal */}
          {waitingForOpponent && (
            <div className="waiting-for-player-modal">
              <div className="waiting-modal-content">
                <div className="waiting-spinner"></div>
                <h3>Waiting for opponent to join...</h3>
                <p>
                  {!currentGame.white_player && !currentGame.black_player
                    ? 'Waiting for players to join'
                    : !currentGame.white_player
                    ? 'Waiting for white player to join'
                    : 'Waiting for black player to join'
                  }
                </p>
                {isCurrentUserInGame && (
                  <div className="waiting-modal-actions">
                    <button
                      type="button"
                      className="cancel-button"
                      onClick={handleCancelWaitingGame}
                      disabled={isCancellingGame}
                    >
                      {isCancellingGame ? 'Cancelling...' : 'Cancel Game'}
                    </button>
                    {moveError && (
                      <div className="error-message waiting-cancel-error">{moveError}</div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Chess board with player info container */}
          <ChessBoard
            game={isViewingHistory ?
              { ...currentGame, board: historicalBoard } :
              (optimisticGame || currentGame)}
            lastMove={isViewingHistory ? null : lastMove}
            onMove={isViewingHistory ? () => {} : handleMove}
            onSquareClick={isViewingHistory ? () => {} : handleSquareClick}
            selectedSquare={isViewingHistory ? null : selectedSquare}
            legalMoves={isViewingHistory ? [] : legalMoves}
            isPlayerTurn={isGameActive && !isViewingHistory}
            orientation={boardOrientation}
            theme={pieceTheme}
            boardTheme={boardTheme}
            teleportTargets={teleportPrompt?.options || []}
            illegalTeleportSquares={illegalTeleportSquares}
            teleportGhostPiece={teleportGhostPiece}
            gameResult={currentGame?.result}
            gameStatus={currentGame?.status}
            allowResize={!isMobileView}
            onPieceSelect={(square) => {
              // Calculate legal moves when a piece is selected for drag
              const board = parseFEN((optimisticGame || currentGame).board)
              const legal = calculateLegalMoves(board, square)
              setSelectedSquare(square)
              setLegalMoves(legal)
            }}
            onPieceDeselect={() => {
              setSelectedSquare(null)
              setLegalMoves([])
            }}
            currentTurn={currentGame?.current_turn}
            isPlayerInGame={((currentGame?.white_player_id === user?.id) || (currentGame?.black_player_id === user?.id))}
            isCurrentPlayerTurn={isPlayerTurn}
            topPlayer={
              boardOrientation === 'white' ? (
                <div className={`board-top-player ${currentGame.current_turn === 'black' ? 'active-player' : ''}`}>
                  <div className="player-name-rating">
                    <div className="player-rating">{getPlayerRating(currentGame.black_player)}</div>
                    <span
                      className={`connection-status-dot ${connectionStatusClass}`}
                      title={`Your connection: ${connectionStatusLabel}`}
                      aria-label={`Your connection: ${connectionStatusLabel}`}
                    />
                    <UserDisplay
                      user={currentGame.black_player}
                      showTitleBadges={true}
                      showStatusBadges={true}
                    />
                  </div>
                  <div className="player-timer">
                    <span className="player-clock">{formatClock(blackClock)}</span>
                  </div>
                </div>
              ) : (
                <div className={`board-top-player ${currentGame.current_turn === 'white' ? 'active-player' : ''}`}>
                  <div className="player-name-rating">
                    <div className="player-rating">{getPlayerRating(currentGame.white_player)}</div>
                    <span
                      className={`connection-status-dot ${connectionStatusClass}`}
                      title={`Your connection: ${connectionStatusLabel}`}
                      aria-label={`Your connection: ${connectionStatusLabel}`}
                    />
                    <UserDisplay
                      user={currentGame.white_player}
                      showTitleBadges={true}
                      showStatusBadges={true}
                    />
                  </div>
                  <div className="player-timer">
                    <span className="player-clock">{formatClock(whiteClock)}</span>
                  </div>
                </div>
              )
            }
            bottomPlayer={
              boardOrientation === 'white' ? (
                <div className={`board-bottom-player ${currentGame.current_turn === 'white' ? 'active-player' : ''}`}>
                  <div className="player-name-rating">
                    <div className="player-rating">{getPlayerRating(currentGame.white_player)}</div>
                    <span
                      className={`connection-status-dot ${connectionStatusClass}`}
                      title={`Your connection: ${connectionStatusLabel}`}
                      aria-label={`Your connection: ${connectionStatusLabel}`}
                    />
                    <UserDisplay
                      user={currentGame.white_player}
                      showTitleBadges={true}
                      showStatusBadges={true}
                    />
                  </div>
                  <div className="player-timer">
                    <span className="player-clock">{formatClock(whiteClock)}</span>
                  </div>
                </div>
              ) : (
                <div className={`board-bottom-player ${currentGame.current_turn === 'black' ? 'active-player' : ''}`}>
                  <div className="player-name-rating">
                    <div className="player-rating">{getPlayerRating(currentGame.black_player)}</div>
                    <span
                      className={`connection-status-dot ${connectionStatusClass}`}
                      title={`Your connection: ${connectionStatusLabel}`}
                      aria-label={`Your connection: ${connectionStatusLabel}`}
                    />
                    <UserDisplay
                      user={currentGame.black_player}
                      showTitleBadges={true}
                      showStatusBadges={true}
                    />
                  </div>
                  <div className="player-timer">
                    <span className="player-clock">{formatClock(blackClock)}</span>
                  </div>
                </div>
              )
            }
          />


          {/* Mobile game actions - shown under players on mobile */}
          <div className="mobile-game-actions">
            {/* Game action buttons for mobile - all in one row */}
            <div className="mobile-action-buttons">
              <button
                className="flip-button"
                onClick={() => setBoardOrientation(prev => prev === 'white' ? 'black' : 'white')}
                title="⇅"
              >
                ⇅
              </button>

              {currentGame.status === 'active' && (
                <>
                  {!playerHasPendingDrawOffer && (
                    <button
                      className="draw-button"
                      disabled={!canOfferDraw}
                      onClick={() => setShowDrawModal(true)}
                    >
                      ½
                    </button>
                  )}
                  {playerHasPendingDrawOffer && (
                    <button
                      className="cancel-draw-button"
                      onClick={handleCancelDraw}
                    >
                      Cancel Draw
                    </button>
                  )}
                  <button
                    className="resign-button"
                    onClick={() => setShowResignModal(true)}
                  >
                    ✕
                  </button>
                </>
              )}
            </div>

            <div className="mobile-logo">
              <img
                src="/images/logo/finallogosquare.png"
                alt="RiftChess"
                className="logo-icon"
              />
            </div>

            {/* Back button for mobile */}
            {onBack && (
              <button
                className="back-button mobile-back-button"
                onClick={onBack}
                style={{
                  width: '50%',
                  marginTop: '0.5rem',
                  marginLeft: 'auto',
                  marginRight: 'auto',
                  display: 'block',
                  textAlign: 'center'
                }}
              >
                Back
              </button>
            )}
          </div>
        </div>

      </div>

      {promotionPrompt && (
        <div className="promotion-modal-backdrop">
          <div className="promotion-modal">
            <h3>Promotion Choice</h3>
            <p>{promotionPrompt.message}</p>
            <div className="promotion-options">
              {promotionPrompt.options.map(option => (
                <button
                  key={option.value}
                  className="promotion-option"
                  onClick={() => handlePromotionSelection(option.value)}
                >
                  {option.image && (
                    <img
                      src={option.image}
                      alt={option.label}
                      className="promotion-piece-image"
                    />
                  )}
                  <span className="promotion-piece-label">{option.label}</span>
                </button>
              ))}
            </div>
            {promotionPrompt.type !== 'pending' && (
              <button
                className="promotion-cancel"
                onClick={handlePromotionCancel}
              >
                Cancel
              </button>
            )}
          </div>
        </div>
      )}

      {/* TOURNAMENT CHECK-IN OVERLAY */}
      {currentGame?.status === 'pregame' && currentGame?.tournament_id && (
        <div className="promotion-modal-backdrop">
          <div className="promotion-modal">
            <h3>Tournament Game - Check In</h3>
            
            <div className="pregame-countdown">
              <div className="countdown-timer">
                <span className="countdown-number">{pregameTimeLeft}</span>
                <span className="countdown-label">seconds remaining</span>
              </div>
            </div>

            <div className="check-in-status">
               {(() => {
                  const isWhite = currentGame.white_player_id === user?.id
                  const isBlack = currentGame.black_player_id === user?.id
                  const amIReady = isWhite ? currentGame.white_ready : currentGame.black_ready
                  const opponentReady = isWhite ? currentGame.black_ready : currentGame.white_ready
                  
                  if (!isWhite && !isBlack) return <p>Waiting for players to check in...</p>

                  if (!amIReady) {
                    return (
                        <>
                          <p>You must check in to start the game.</p>
                          <button 
                            className="promotion-option" 
                            style={{marginTop: '20px', background: '#4CAF50'}}
                            onClick={handleCheckIn}
                          >
                            I'M READY
                          </button>
                        </>
                    )
                  } else {
                     return (
                         <div style={{marginTop: '20px'}}>
                            <p>✅ You are checked in!</p>
                            <p style={{marginTop: '10px', opacity: 0.8}}>
                                {opponentReady ? "Opponent is ready. Game starting..." : "Waiting for opponent to check in..."}
                            </p>
                            {/* Spinner */}
                            <div className="waiting-spinner" style={{margin: '20px auto'}}></div>
                         </div>
                     )
                  }
               })()}
            </div>
            
            <button
               className="promotion-cancel"
               style={{marginTop: '30px'}}
               onClick={onBack}
            >
               Go Back
            </button>
          </div>
        </div>
      )}

      {showCancellationPopup && (
        <div className="promotion-modal-backdrop">
          <div className="promotion-modal">
            <h3>Game Cancelled</h3>
            <p>{cancellationMessage || 'This game was cancelled before it began.'}</p>
            <button
              className="promotion-option"
              onClick={() => {
                setShowCancellationPopup(false)
                if (onBack) onBack()
              }}
            >
              Back to Dashboard
            </button>
          </div>
        </div>
      )}


      {showResignModal && (
        <div className="promotion-modal-backdrop">
          <div className="promotion-modal">
            <h3>Confirm Resignation</h3>
            <p>Are you sure you want to resign this game?</p>
            <div className="promotion-options">
              <button
                className="promotion-option danger"
                onClick={handleResign}
              >
                Yes
              </button>
              <button
                className="promotion-cancel"
                onClick={() => setShowResignModal(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {showDrawModal && (
        <div className="promotion-modal-backdrop">
          <div className="promotion-modal">
            <h3>½</h3>
            <p>Do you want to offer a draw to your opponent?</p>
            <div className="promotion-options">
              <button
                className="promotion-option"
                onClick={handleOfferDraw}
              >
                ½
              </button>
              <button
                className="promotion-cancel"
                onClick={() => setShowDrawModal(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {showIncomingDrawModal && opponentHasPendingDrawOffer && (
        <div className="promotion-modal-backdrop">
          <div className="promotion-modal">
            <h3>Draw Offer</h3>
            <p><UserDisplay user={getOpponentPlayer()} showTitleBadges={false} showStatusBadges={true} /> is offering a draw. How would you like to respond?</p>
            <div className="promotion-options">
              <button
                className="promotion-option"
                onClick={() => handleDrawResponse('accept')}
              >
                Accept Draw
              </button>
              <button
                className="promotion-cancel"
                onClick={() => handleDrawResponse('decline')}
              >
                Decline
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Rematch Modal */}
      {showRematchModal && rematchRequestedBy && (
        <div className="promotion-modal-backdrop">
          <div className="promotion-modal">
            <h3>Rematch Request</h3>
            <p>
              {rematchRequestedBy === user?.id ? 'You' : 'Your opponent'}
              {rematchRequestedBy !== user?.id ? ' has requested a rematch!' : ' have requested a rematch.'}
            </p>
            {rematchRequestedBy !== user?.id && (
              <div className="modal-buttons">
                <button
                  className="accept-button"
                  onClick={handleAcceptRematch}
                >
                  Accept
                </button>
                <button
                  className="cancel-button"
                  onClick={handleDeclineRematch}
                >
                  Decline
                </button>
              </div>
            )}
            {rematchRequestedBy === user?.id && (
              <p className="waiting-message">Waiting for opponent's response...</p>
            )}
          </div>
        </div>
      )}

      {/* Rules Modal */}
      {showRulesModal && selectedRule && (
        <div className="rules-modal-backdrop" onClick={closeRulesModal}>
          <div className="rules-modal" onClick={(e) => e.stopPropagation()}>
            <div className="rules-modal-header">
              <h3 className={`modal-title-${selectedRule}`}>
                <div className="modal-title-with-icon">
                  <div className={`modal-legend-color ${selectedRule}`}>
                    <span className="modal-legend-icon">
                      {selectedRule === 'death' && '💀'}
                      {selectedRule === 'teleport' && '✦'}
                      {selectedRule === 'promotion' && '↗'}
                    </span>
                  </div>
                  {getRuleContent(selectedRule).title}
                </div>
              </h3>
              <button className="close-button" onClick={closeRulesModal}>×</button>
            </div>
            <div className="rules-modal-content">
              <p>{getRuleContent(selectedRule).description}</p>
            </div>
          </div>
        </div>
      )}

      {/* Game Cancellation Popup */}
      {showCancellationPopup && (
        <div className="promotion-modal-backdrop">
          <div className="promotion-modal game-cancelled-modal">
            <h3>Game Cancelled</h3>
            <p>{cancellationMessage}</p>
            <p className="redirect-message">Returning to dashboard...</p>
          </div>
        </div>
      )}



      {/* Reconnecting overlay */}
      {isReconnecting && (
        <div className="connection-modal-backdrop">
          <div className="connection-modal">
            <div className="connection-spinner"></div>
            <h3>Reconnecting…</h3>
            <p>Hold on while we restore your connection.</p>
          </div>
        </div>
      )}

      {/* Android Mobile Connection Status */}
      {/Android/i.test(navigator.userAgent) && connectionStatus !== 'connected' && (
        <div className="mobile-connection-alert">
          <div className="connection-status">
            <span className="status-indicator error"></span>
            <span>Connection: {connectionStatus}</span>
            <div className="connection-actions">
              <button
                className="reconnect-btn"
                onClick={() => {
                  console.log('Manual reconnection triggered');
                  connect();
                }}
              >
                Reconnect
              </button>
              <button
                className="refresh-btn"
                onClick={() => {
                  console.log('Manual game refresh triggered');
                  if (gameId) {
                    getGame(gameId);
                  }
                }}
              >
                Refresh Game
              </button>
            </div>
          </div>
          <p className="connection-hint">
            Android connection issues? Try reconnecting or refreshing the game state.
          </p>
        </div>
      )}

    </div>
  )
}

export default Game
